<template>
  <div class="exam-grading-page">
    <!-- 顶部标题栏 -->
    <div class="grading-header">
      <!-- <span class="title">数据审核</span> -->
      
      <!-- 当前店铺信息 -->
      <div class="current-dealer-info" v-if="currentExaminee && (currentExaminee.dealerCode || currentExaminee.dealerName)" style="margin-left: 0;">
         <span class="dealer-code">{{ currentExaminee.dealerCode }}</span>
         <span class="dealer-name">{{ currentExaminee.dealerName }}</span>
      </div>

      <!-- 审核级别切换（仅考试问卷显示） -->
      <div class="review-level-tabs" v-if="phase === 0 || examForm === 1">
        <el-radio-group v-model="reviewLevel" size="small" @change="handleReviewLevelChange">
          <el-radio-button :label="1" :disabled="!canAccessLevel(1)">一审</el-radio-button>
          <el-radio-button :label="2" :disabled="!canAccessLevel(2)">二审</el-radio-button>
          <el-radio-button :label="3" :disabled="!canAccessLevel(3)">三审</el-radio-button>
        </el-radio-group>
        <!-- 状态提示区域 - 固定宽度，避免布局跳动 -->
        <span class="level-hint-wrapper">
          <span class="level-hint readonly-hint" v-if="currentExaminee && isReadOnlyMode">
            <i class="el-icon-view"></i> 只读模式
          </span>
          <span class="level-hint warning" v-else-if="currentExaminee && !canEditCurrentLevel && needPreviousLevel">
            <i class="el-icon-warning"></i> 需先完成{{ previousLevelText }}审
          </span>
          <span class="level-hint warning" v-else-if="currentExaminee && !canEditCurrentLevel">
            <i class="el-icon-warning"></i> 当前级别已完成
          </span>
        </span>
      </div>
      <div class="review-level-tabs" v-else>
        <span style="font-size: 14px; font-weight: 500; color: #303133;">资料审核</span>
        <span class="level-hint-wrapper">
          <span class="level-hint readonly-hint" v-if="currentExaminee && isReadOnlyMode">
            <i class="el-icon-check" style="color: #67C23A"></i> 已审核
          </span>
        </span>
      </div>
    </div>

    <!-- 三栏布局 -->
    <div class="grading-content">
      <!-- 左侧：考生列表 -->
      <div class="left-panel">
        <div class="panel-header">
          <span class="panel-title">审核名单</span>
          <span class="progress">{{ gradedCount }}/{{ totalCount }}</span>
        </div>
        
        <div class="examinee-list">
          <div
            v-for="(item, index) in filteredExamineeList"
            :key="item.answerKey"
            class="examinee-item"
            :class="{ active: currentExaminee && currentExaminee.answerKey === item.answerKey }"
            @click="selectExaminee(item)"
          >
            <span class="examinee-name">{{ item.dealerCode || ('答卷' + (index + 1)) }}</span>
            <span class="draft-badge" v-if="hasDraft(item)">草稿</span>
            <span class="status-badge" :class="getStatusClass(item)">
              {{ getStatusText(item) }}
            </span>
          </div>
        </div>
        
        <div class="left-panel-footer">
          <el-select v-model="statusFilter" size="mini" placeholder="筛选状态" clearable style="width: 100%;">
            <el-option label="全部" value="" />
            <el-option label="待审核" :value="0" />
            <template v-if="isSimplifiedAudit">
              <el-option label="已通过" :value="1" />
            </template>
            <template v-else>
              <el-option label="一审完成" :value="1" />
              <el-option label="二审完成" :value="2" />
              <el-option label="三审完成" :value="3" />
            </template>
          </el-select>
          <div class="footer-info" v-if="currentExaminee">
            <p>答题时长：{{ formatDuration(currentExaminee.durationSeconds) }}</p>
            <p>提交时间：{{ currentExaminee.submitTime }}</p>
            <p>当前状态：{{ getStatusText(currentExaminee) }}</p>
          </div>
        </div>
      </div>

      <!-- 中间：答题详情 + 底部按钮 -->
      <section class="center-section">
      <div class="center-panel" v-loading="detailLoading" element-loading-text="加载答题详情...">
        <div v-if="currentExaminee" class="answer-list">
          
          <!-- ===== 跨阶段参考资料区域（仅考试模式显示，简化审核不需要） ===== -->
          <div v-if="!isSimplifiedAudit && crossPhaseGroups.length > 0" class="cross-phase-reference">
            <div class="ref-section-title">
              <i class="el-icon-folder-opened"></i>
              <span>参考资料（其他阶段）</span>
            </div>
            
            <div v-for="group in crossPhaseGroups" :key="group.phaseNo" class="ref-phase-group">
              <div class="ref-phase-header">
                <span class="ref-phase-badge">{{ group.phaseNo }}</span>
                <span class="ref-phase-name">{{ group.phaseName }}</span>
                <span class="ref-phase-time" v-if="group.submitTime">提交于 {{ group.submitTime }}</span>
              </div>
              
              <div v-for="item in group.items" :key="item.itemKey" class="ref-question-card"
                   :ref="'ref-item-' + group.phaseNo + '-' + item.itemKey">
                <div class="ref-question-title">
                  <span v-if="item.serialNo" class="ref-serial">{{ item.serialNo }}.</span>
                  {{ stripHtml(item.label) }}
                </div>
                
                <div class="ref-answer-area">
                  <!-- 选择题：显示选中项 -->
                  <template v-if="isCrossPhaseChoiceQuestion(item.itemType)">
                    <div v-for="opt in item.options" :key="opt.value" class="ref-option"
                         :class="{ selected: isCrossPhaseOptionSelected(item, opt) }">
                      <i v-if="isCrossPhaseOptionSelected(item, opt)" class="el-icon-check" style="color: #2563eb; font-weight: bold;"></i>
                      <span v-else class="ref-option-circle"></span>
                      <span>{{ stripHtml(opt.label) }}</span>
                    </div>
                  </template>
                  
                  <!-- 文件上传题：图片/视频/录音 -->
                  <template v-else-if="isFileUploadQuestion(item.itemType) || isAudioRecordQuestion(item.itemType)">
                    <template v-if="getCrossPhaseAnswerValue(item)">
                      <div class="ref-file-gallery">
                        <template v-for="(file, fIdx) in parseRefFileList(getCrossPhaseAnswerValue(item))">
                          <div v-if="isImage(file.url)" :key="'ri-'+fIdx" class="ref-file-thumb"
                               @click="openFloatingMedia('image', file.url, item.serialNo + '. ' + stripHtml(item.label))">
                            <img :src="file.url" />
                            <span class="ref-thumb-overlay"><i class="el-icon-zoom-in"></i></span>
                          </div>
                          <div v-else-if="isAudio(file.url)" :key="'ra-'+fIdx" class="ref-file-thumb ref-audio-thumb"
                               @click="openFloatingMedia('audio', file.url, file.name || '录音')">
                            <i class="el-icon-headset"></i>
                            <span>{{ file.name || '录音' }}</span>
                          </div>
                          <div v-else-if="isVideo(file.url)" :key="'rv-'+fIdx" class="ref-file-thumb ref-video-thumb"
                               @click="openFloatingMedia('video', file.url, file.name || '视频')">
                            <i class="el-icon-video-play"></i>
                            <span>{{ file.name || '视频' }}</span>
                          </div>
                          <div v-else-if="isPdf(file.url)" :key="'rp-'+fIdx" class="ref-file-thumb ref-pdf-thumb"
                               @click="openFloatingMedia('pdf', file.url, file.name || 'PDF材料')">
                            <i class="el-icon-document"></i>
                            <span>{{ file.name || 'PDF材料' }}</span>
                          </div>
                          <a v-else :key="'rf-'+fIdx" :href="file.url" target="_blank" class="ref-file-link">
                            <i class="el-icon-document"></i> {{ file.name || '文件' }}
                          </a>
                        </template>
                      </div>
                    </template>
                    <!-- 纯 URL 录音（非JSON格式） -->
                    <template v-else-if="isAudioRecordQuestion(item.itemType) && getCrossPhaseAnswerValue(item)">
                      <div class="ref-file-thumb ref-audio-thumb"
                           @click="openFloatingMedia('audio', getCrossPhaseAnswerValue(item), '录音')">
                        <i class="el-icon-headset"></i>
                        <span>播放录音</span>
                      </div>
                    </template>
                    <span v-else class="ref-no-answer">未作答</span>
                  </template>
                  
                  <!-- 评分题 -->
                  <template v-else-if="isRatingQuestion(item.itemType)">
                    <el-rate v-if="getCrossPhaseAnswerValue(item)"
                             :value="Number(getCrossPhaseAnswerValue(item)) || 0" disabled show-score text-color="#ff9900" />
                    <span v-else class="ref-no-answer">未作答</span>
                  </template>
                  
                  <!-- 签名题 -->
                  <template v-else-if="isSignatureQuestion(item.itemType)">
                    <div v-if="getCrossPhaseAnswerValue(item)" class="ref-signature-container"
                         @click="openFloatingMedia('image', getCrossPhaseAnswerValue(item), '签名')">
                      <img :src="getCrossPhaseAnswerValue(item)" />
                    </div>
                    <span v-else class="ref-no-answer">未签名</span>
                  </template>
                  
                  <!-- 廉政协议题 -->
                  <template v-else-if="item.itemType === 'INTEGRITY_AGREEMENT'">
                    <div v-if="getCrossPhaseAnswerValue(item)">
                      <!-- 廉政协议为图片时直接展示 -->
                      <div v-if="isAgreementImage(parseAgreementAnswer(getCrossPhaseAnswerValue(item)).agreementUrl)" class="ref-agreement-image"
                           @click="openFloatingMedia('image', parseAgreementAnswer(getCrossPhaseAnswerValue(item)).agreementUrl, '廉政协议')">
                        <img :src="parseAgreementAnswer(getCrossPhaseAnswerValue(item)).agreementUrl" style="max-width:100%;border-radius:4px;cursor:pointer;" />
                      </div>
                      <!-- 廉政协议为 PDF 时渲染 -->
                      <div v-else class="ref-agreement-pdf" :ref="'refPdf_' + group.phaseNo + '_' + item.itemKey">
                        <div v-if="refPdfStates[group.phaseNo + '_' + item.itemKey] === 'loading'" style="color:#94a3b8;font-size:13px;padding:8px 0;"><i class="el-icon-loading"></i> PDF 加载中...</div>
                        <div v-if="refPdfStates[group.phaseNo + '_' + item.itemKey] === 'error'" style="color:#f56c6c;font-size:13px;padding:8px 0;"><i class="el-icon-warning-outline"></i> PDF 加载失败</div>
                      </div>
                      <div class="ref-agreement-status" style="margin-top:8px;">
                        <template v-if="parseAgreementAnswer(getCrossPhaseAnswerValue(item)).checked">
                          <i class="el-icon-circle-check" style="color:#67c23a;margin-right:4px;"></i>
                          <span style="color:#67c23a;font-weight:600;">已确认同意廉政协议</span>
                        </template>
                        <template v-else>
                          <i class="el-icon-circle-close" style="color:#f56c6c;margin-right:4px;"></i>
                          <span style="color:#f56c6c;">未确认</span>
                        </template>
                      </div>
                    </div>
                    <span v-else class="ref-no-answer">未作答</span>
                  </template>
                  
                  <!-- 手机号验证题 -->
                  <template v-else-if="isPhoneVerifyQuestion(item.itemType)">
                    <div v-if="getCrossPhaseAnswerValue(item)" class="ref-phone-answer">
                      <span>手机号：{{ parsePhoneAnswer(getCrossPhaseAnswerValue(item)).phone || '-' }}</span>
                      <span v-if="parsePhoneAnswer(getCrossPhaseAnswerValue(item)).code" style="margin-left:12px;">验证码：{{ parsePhoneAnswer(getCrossPhaseAnswerValue(item)).code }}</span>
                    </div>
                    <span v-else class="ref-no-answer">未作答</span>
                  </template>
                  
                  <!-- 其他题型（文本等） -->
                  <template v-else>
                    <span v-if="getCrossPhaseAnswerValue(item)" class="ref-text-answer">{{ formatAnswerDisplay(getCrossPhaseAnswerValue(item)) }}</span>
                    <span v-else class="ref-no-answer">未作答</span>
                  </template>
                </div>
              </div>
            </div>
          </div>
          
          <div v-if="crossPhaseLoading" class="cross-phase-loading">
            <i class="el-icon-loading"></i> 加载其他阶段资料...
          </div>
          
          <!-- ===== 当前审核阶段标记 ===== -->
          <div v-if="crossPhaseGroups.length > 0" class="current-audit-marker">
            <span class="audit-marker-badge">当前审核</span>
            <span class="audit-marker-text">{{ phase > 0 ? '阶段' + phase : '' }}</span>
          </div>
          
          <!-- ===== 原有的审核题目列表 ===== -->
          <div
            v-for="(question, qIndex) in filteredQuestionList"
            :key="question.itemKey"
            :ref="'question-' + qIndex"
            class="question-card"
          >
            <div class="question-header">
              <span class="question-title">{{ question.serialNo ? question.serialNo + '.' + stripHtml(question.label || '') + ' 【' + question.typeName + '】' : '第' + (qIndex + 1) + '题 【' + question.typeName + '】' }}</span>
              <span class="question-score-info" v-if="question.enableScoring && question.maxScore">
                ({{ question.maxScore }}分)
              </span>
              <span class="full-record-badge" v-if="isAudioRecordQuestion(question.itemType)">
                <i class="dot"></i> 全程录音
              </span>
              <el-button v-if="isAudioRecordQuestion(question.itemType) && getAnswerValue(question.itemKey)"
                type="text" size="mini" icon="el-icon-position" 
                @click="openFloatingAudio(question)" 
                class="float-play-btn">悬浮播放</el-button>
            </div>
            
            <div class="question-content">
              <div class="question-label" v-if="!question.serialNo">{{ stripHtml(question.label) }}</div>
              
              <!-- 选择题选项 -->
              <div v-if="isChoiceQuestion(question.itemType) && !isMingjianPhotoQuestion(question.itemType)" class="options-list">
                <div v-if="!canEditReviewAnswer(question)" class="loopback-hint">
                  未配置回圈，当前题目不可修改答案
                </div>
                <template v-for="opt in question.options">
                  <div
                    :key="opt.value"
                    class="option-item"
                    :class="{
                      'clickable': canEditReviewAnswer(question),
                      'is-review-selected': isReviewSelected(question, opt),
                      'is-original-selected': canShowOriginalForQuestion(question) && !isReviewSelected(question, opt) && isOriginalSelected(question, opt)
                    }"
                    @click="canEditReviewAnswer(question) && toggleReviewOption(question, opt)"
                  >
                    <span class="option-marker">
                      <i v-if="isReviewSelected(question, opt)" class="el-icon-check review-check"></i>
                      <i v-else-if="canShowOriginalForQuestion(question) && !isReviewSelected(question, opt) && isOriginalSelected(question, opt)" class="el-icon-check" style="color: #409EFF; font-weight: bold;"></i>
                      <span v-else-if="isMultipleChoice(question.itemType)" class="option-checkbox"></span>
                      <span v-else class="option-circle"></span>
                    </span>
                    <span class="option-text">{{ stripHtml(opt.label) }}</span>
                    <span v-if="canShowOriginalForQuestion(question) && isOriginalSelected(question, opt)" class="original-tag">原答案</span>
                    <span v-if="isReview1Selected(question, opt)" class="review1-tag">一审</span>
                    <span v-if="isReview2Selected(question, opt)" class="review2-tag">二审</span>
                  </div>
                  <!-- "其他"选项的文本输入框（只读显示） -->
                  <div v-if="isOtherOption(opt)" :key="'other-' + opt.value" class="other-input-wrapper">
                    <el-input
                      v-model="otherTextMap[question.itemKey]"
                      class="other-input"
                      size="small"
                      placeholder="请输入其他内容"
                      disabled
                    />
                  </div>
                </template>
              </div>
              
              <!-- 评分类题目：显示星星 -->
              <div v-else-if="isRatingQuestion(question.itemType)" class="subjective-answer">
                <div v-if="question.description || question.tipContent" class="question-description" style="color: #e6a23c; font-size: 13px; margin-bottom: 10px; line-height: 1.6;">{{ stripHtml(question.description || question.tipContent) }}</div>
                <div class="answer-content rating-display">
                  <el-rate 
                    v-if="getAnswerValue(question.itemKey)"
                    :value="Number(getAnswerValue(question.itemKey)) || 0" 
                    disabled 
                    :max="getRatingMax(question)"
                    show-score
                    text-color="#ff9900"
                  />
                  <span v-else>未作答</span>
                </div>
              </div>
              
              <!-- 录音题答案展示 -->
              <div v-else-if="isAudioRecordQuestion(question.itemType)" class="audio-answer">
                <div class="answer-content" v-if="getAnswerValue(question.itemKey)">
                  <AudioPlayer 
                    ref="audioPlayer"
                    :src="getAudioUrl(question.itemKey)" 
                    :filename="getAudioFilename(question, currentExaminee)"
                  />
                </div>
                <div class="answer-content" v-else>未作答</div>
              </div>
              
              <!-- 签名题：显示签名图片 -->
              <div v-else-if="isSignatureQuestion(question.itemType)" class="subjective-answer">
                <div class="answer-label">考生签名：</div>
                <div class="answer-content" v-if="getAnswerValue(question.itemKey)">
                  <img 
                    :src="getAnswerValue(question.itemKey)" 
                    alt="签名" 
                    style="max-width: 300px; max-height: 150px; border: 1px solid #e4e7ed; border-radius: 4px; background: #fff; padding: 4px; cursor: pointer;"
                    @click="openFloatingMedia('image', getAnswerValue(question.itemKey), '签名')"
                  />
                </div>
                <div class="answer-content" v-else>未签名</div>
              </div>
              
              <!-- 廉政协议题 -->
              <div v-else-if="question.itemType === 'INTEGRITY_AGREEMENT'" class="agreement-answer-section">
                <!-- 廉政协议为图片时直接展示 -->
                <div v-if="isAgreementImage(parseAgreementValue(question.itemKey).agreementUrl)"
                     @click="openFloatingMedia('image', parseAgreementValue(question.itemKey).agreementUrl, '廉政协议')">
                  <img :src="parseAgreementValue(question.itemKey).agreementUrl" style="max-width:100%;border-radius:4px;cursor:pointer;" />
                </div>
                <!-- 廉政协议为 PDF 时渲染 -->
                <div v-else-if="parseAgreementValue(question.itemKey).agreementUrl" class="agreement-pdf-pages" :ref="'gradingPdf_' + question.itemKey">
                  <div v-if="gradingPdfStates[question.itemKey] === 'loading'" class="pdf-loading"><i class="el-icon-loading"></i> PDF 加载中...</div>
                  <div v-if="gradingPdfStates[question.itemKey] === 'error'" class="pdf-error"><i class="el-icon-warning-outline"></i> PDF 加载失败</div>
                </div>
                <div class="agreement-check-status">
                  <i :class="parseAgreementValue(question.itemKey).checked ? 'el-icon-success' : 'el-icon-warning'" 
                     :style="{ color: parseAgreementValue(question.itemKey).checked ? '#67c23a' : '#e6a23c' }"></i>
                  <span>{{ parseAgreementValue(question.itemKey).checked ? '已确认同意廉政协议' : '未确认' }}</span>
                </div>
              </div>
              
              <!-- 手机号验证题 -->
              <div v-else-if="isPhoneVerifyQuestion(question.itemType)" class="subjective-answer">
                <div class="answer-content" v-if="getAnswerValue(question.itemKey)">
                  <span>手机号：{{ parsePhoneAnswer(getAnswerValue(question.itemKey)).phone || '-' }}</span>
                  <span v-if="parsePhoneAnswer(getAnswerValue(question.itemKey)).code" style="margin-left:12px;">验证码：{{ parsePhoneAnswer(getAnswerValue(question.itemKey)).code }}</span>
                </div>
                <div class="answer-content" v-else>未作答</div>
              </div>
              
              <!-- 明检分类题型：显示拍照图片 + 分类结果 -->
              <div v-else-if="isMingjianPhotoQuestion(question.itemType)" class="mingjian-answer-section">
                <!-- 拍照图片区 -->
                <div class="mingjian-photos" v-if="getMingjianPhotos(question.itemKey).length > 0">
                  <div class="mingjian-section-label"><i class="el-icon-camera"></i> 拍照取证</div>
                  <div class="file-gallery">
                    <div v-for="(photo, pIdx) in getMingjianPhotos(question.itemKey)" :key="'mj-'+pIdx"
                         class="file-thumb image-thumb" style="cursor:pointer;"
                         @click="openFloatingMedia('image', photo.url, photo.name || '取证图片')"  >
                      <img :src="photo.url" :alt="photo.name || '取证图片'" />
                    </div>
                  </div>
                </div>
                <!-- 分类结果 -->
                <div class="mingjian-result">
                  <div class="mingjian-section-label"><i class="el-icon-s-grid"></i> 分类结果</div>
                  <div v-if="question.itemType === 'MINGJIAN_PHOTO_TEXT'" class="mingjian-text-review">
                    <div class="answer-content" style="margin-bottom: 8px;">
                      <span style="color: #909399; font-size: 12px;">原答案：</span>{{ getMingjianTextValue(question.itemKey) || '未填写' }}
                    </div>
                    <div v-if="canEditReviewAnswer(question)" style="margin-top: 4px;">
                      <el-input
                        type="textarea"
                        :rows="2"
                        placeholder="请输入审核答案"
                        :value="reviewAnswerMap[question.itemKey] || ''"
                        @input="val => $set(reviewAnswerMap, question.itemKey, val)"
                        :disabled="isReadOnlyMode"
                        size="small"
                      />
                    </div>
                  </div>
                  <div v-else class="options-list">
                    <div v-for="opt in question.options" :key="opt.value" class="option-item"
                         :class="{
                           'clickable': canEditReviewAnswer(question),
                           'is-review-selected': isReviewSelected(question, opt),
                           'is-original-selected': canShowOriginalForQuestion(question) && !isReviewSelected(question, opt) && isMingjianOriginalSelected(question, opt)
                         }"
                         @click="canEditReviewAnswer(question) && toggleReviewOption(question, opt)">
                      <span class="option-marker">
                        <i v-if="isReviewSelected(question, opt)" class="el-icon-check review-check"></i>
                        <i v-else-if="canShowOriginalForQuestion(question) && isMingjianOriginalSelected(question, opt)" class="el-icon-check" style="color: #409EFF; font-weight: bold;"></i>
                        <span v-else-if="isMultipleChoice(question.itemType)" class="option-checkbox"></span>
                        <span v-else class="option-circle"></span>
                      </span>
                      <span class="option-text">{{ stripHtml(opt.label) }}</span>
                      <span v-if="canShowOriginalForQuestion(question) && isMingjianOriginalSelected(question, opt)" class="original-tag">原答案</span>
                    </div>
                  </div>
                  <!-- "其他"补充文本展示 -->
                  <div v-if="getMingjianOtherText(question.itemKey)" class="mingjian-other-text" style="margin-top: 6px; padding: 6px 10px; background: #fdf6ec; border-radius: 4px; font-size: 13px; color: #e6a23c;">
                    <i class="el-icon-edit"></i> 其他补充：{{ getMingjianOtherText(question.itemKey) }}
                  </div>
                </div>
              </div>
              
              <!-- 其他主观题答案 -->
              <div v-else-if="!isFileUploadQuestion(question.itemType)" class="subjective-answer">
                <div class="answer-content">{{ formatAnswerDisplay(getAnswerValue(question.itemKey)) || '未作答' }}</div>
              </div>
              
              <!-- 文件上传题的内容展示 -->
              <div v-else class="file-upload-answer">
                <div class="answer-label">上传文件：</div>
                <div class="file-gallery" v-viewer="{movable: false}" v-if="getFileList(question.itemKey).length > 0">
                  <template v-for="(file, fIdx) in getFileList(question.itemKey)">
                    <!-- 图片预览 -->
                    <div v-if="isImage(file.url)" :key="'img-' + fIdx" class="file-thumb image-thumb" 
                         style="cursor: pointer;" @click="openFloatingMedia('image', file.url, file.name || '图片')">
                      <img :src="file.url" :alt="file.name || '图片'" />
                    </div>
                    <!-- 视频播放器 -->
                    <div v-else-if="isVideo(file.url)" :key="'vid-' + fIdx" class="file-thumb video-thumb"
                         style="cursor: pointer;" @click="openFloatingMedia('video', file.url, file.name || '视频')">
                      <video :src="file.url" controls preload="metadata" style="max-width: 300px; max-height: 200px;"></video>
                    </div>
                    <!-- 音频播放器 -->
                    <div v-else-if="isAudio(file.url)" :key="'aud-' + fIdx" class="file-thumb audio-thumb"
                         style="cursor: pointer;" @click="openFloatingMedia('audio', file.url, file.name || '音频文件')">
                      <audio :src="file.url" controls preload="metadata" style="width: 250px;"></audio>
                      <span class="audio-name">{{ file.name || '音频文件' }}</span>
                    </div>
                    <!-- PDF 在线查看 -->
                    <div v-else-if="isPdf(file.url)" :key="'pdf-' + fIdx" class="file-thumb pdf-thumb"
                         style="cursor: pointer;" @click="openFloatingMedia('pdf', file.url, file.name || 'PDF文件')">
                      <i class="el-icon-document"></i>
                      <span class="audio-name">{{ file.name || 'PDF文件' }}</span>
                    </div>
                    <!-- 其他文件下载 -->
                    <a v-else :key="'file-' + fIdx" :href="file.url" target="_blank" class="file-download-link">
                      <i class="el-icon-document"></i>
                      <span>{{ file.name || '下载文件' }}</span>
                    </a>
                  </template>
                </div>
                <div v-else class="no-file">未上传文件</div>
              </div>
            </div>
            
            <!-- 审核评分区域（评分题 或 简化审核模式，但排除RATING主观评分题） -->
            <div class="review-section" v-if="(question.enableScoring || isSimplifiedAudit) && !isRatingQuestion(question.itemType)">
              <!-- 原答案特殊情况说明（仅考试审核模式） -->
              <div v-if="!isSimplifiedAudit && specialRemarkMap[question.itemKey]" class="guest-special-remark" style="margin-bottom: 8px; padding: 10px; background: #FFF0F0; border: 1px dashed #FF4D4F; border-radius: 4px; color: #FF4D4F;">
                <strong>原答案 特殊情况说明：</strong>
                <span>{{ specialRemarkMap[question.itemKey] }}</span>
              </div>
              
              <!-- 历史审核说明（仅考试审核模式） -->
              <div v-if="!isSimplifiedAudit && reviewLevel > 1 && specialRemark1Map[question.itemKey]" class="history-special-remark" style="margin-bottom: 8px; padding: 10px; background: #E6F7FF; border: 1px dashed #1890FF; border-radius: 4px; color: #1890FF;">
                <strong>一审 特殊情况说明：</strong>
                <span>{{ specialRemark1Map[question.itemKey] }}</span>
              </div>
              <div v-if="!isSimplifiedAudit && reviewLevel > 2 && specialRemark2Map[question.itemKey]" class="history-special-remark" style="margin-bottom: 8px; padding: 10px; background: #FFF7E6; border: 1px dashed #FA8C16; border-radius: 4px; color: #FA8C16;">
                <strong>二审 特殊情况说明：</strong>
                <span>{{ specialRemark2Map[question.itemKey] }}</span>
              </div>
              
              <div class="review-options">
                <span class="review-label">{{ isSimplifiedAudit ? '审核结果：' : '评审结果：' }}</span>
                <el-radio-group 
                  v-model="reviewMap[question.itemKey]" 
                  size="small" 
                  @change="handleReviewChange(question, $event)"
                  :disabled="isReadOnlyMode || isReviewLocked(question)"
                >
                  <template v-if="isSimplifiedAudit">
                    <!-- 简化审核：通过/不通过 -->
                    <el-radio label="SCORE">
                      <i class="el-icon-check" style="color: #67C23A"></i> 通过
                    </el-radio>
                    <el-radio label="NO_SCORE">
                      <i class="el-icon-close" style="color: #F56C6C"></i> 不通过
                    </el-radio>
                  </template>
                  <template v-else>
                    <!-- 评分审核：得分/不得分/不涉及 -->
                    <el-radio label="SCORE" :disabled="!canSelectScore(question)">
                      <i class="el-icon-check" style="color: #67C23A"></i> 得分
                    </el-radio>
                    <el-radio label="NO_SCORE">
                      <i class="el-icon-close" style="color: #F56C6C"></i> 不得分
                    </el-radio>
                    <el-radio label="NOT_APPLICABLE" :disabled="!canSelectNotApplicable(question)">
                      <i class="el-icon-minus" style="color: #909399"></i> 不涉及
                    </el-radio>
                  </template>
                </el-radio-group>
              </div>
              <!-- 备注区域 -->
              <div class="review-remark" style="margin-top: 8px;">
                <!-- 历史失分备注（仅考试审核模式） -->
                <div v-if="!isSimplifiedAudit && reviewLevel > 1 && remark1Map[question.itemKey]" style="margin-bottom: 8px; padding: 10px; background: #F5F5F5; border: 1px dashed #909399; border-radius: 4px; color: #606266;">
                  <strong>一审 失分备注：</strong>
                  <span>{{ remark1Map[question.itemKey] }}</span>
                </div>
                <div v-if="!isSimplifiedAudit && reviewLevel > 2 && remark2Map[question.itemKey]" style="margin-bottom: 8px; padding: 10px; background: #F5F5F5; border: 1px dashed #909399; border-radius: 4px; color: #606266;">
                  <strong>二审 失分备注：</strong>
                  <span>{{ remark2Map[question.itemKey] }}</span>
                </div>
                
                <!-- 当前备注输入 -->
                <div class="current-input">
                  <div style="margin-bottom: 5px; display: flex; align-items: center;">
                    <span style="font-size: 13px; font-weight: 500; color: #606266;">{{ isSimplifiedAudit ? '审核备注' : '失分备注' }}</span>
                    <span v-if="reviewMap[question.itemKey] === 'NO_SCORE'" style="color: #F56C6C; margin-left: 5px; font-size: 12px;">* (该题不得分，请填写失分原因)</span>
                    <span v-else style="color: #909399; margin-left: 5px; font-size: 12px;">（可选）</span>
                  </div>
                  <el-input
                    type="textarea"
                    v-model="remarkMap[question.itemKey]"
                    :placeholder="isSimplifiedAudit ? '请填写不通过原因' : '请填写失分备注说明'"
                    :rows="2"
                    maxlength="500"
                    show-word-limit
                    size="small"
                    :disabled="isReadOnlyMode || (!isSimplifiedAudit && reviewMap[question.itemKey] === 'SCORE')"
                  />
                </div>
              </div>

              <!-- 特殊情况说明（仅考试审核模式显示） -->
              <div class="review-special-remark" v-if="!isSimplifiedAudit && formEnableSpecialRemark && question.enableScoring" style="margin-top: 8px; margin-bottom: 8px;">
                <!-- 当前输入框 -->
                <div class="current-input">
                  <div style="margin-bottom: 5px; display: flex; align-items: center;">
                    <span style="font-size: 13px; font-weight: 500; color: #606266;">特殊情况说明</span>
                    <span style="color: #909399; margin-left: 5px; font-size: 12px;">（可选）</span>
                  </div>
                  <el-input
                    type="textarea"
                    v-model="reviewSpecialRemarkMap[question.itemKey]"
                    :placeholder="reviewMap[question.itemKey] === 'NO_SCORE' ? '此题为扣分项，请填写特殊情况说明' : '如有特殊情况请在此说明（可选）'"
                    :rows="2"
                    maxlength="500"
                    show-word-limit
                    size="small"
                    :disabled="isReadOnlyMode"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div v-else class="empty-tip">
          <p>请从左侧选择一份试卷进行阅卷</p>
        </div>
      </div>

      <!-- 底部固定按钮栏（仅中间区域） -->
      <div class="grading-footer">
        <el-button 
          v-if="!isReadOnlyMode"
          type="success" 
          size="medium" 
          @click="handleSubmitReview" 
          :disabled="!canEditCurrentLevel || submitLoading"
          :loading="submitLoading"
        >
          <i v-if="!submitLoading" class="el-icon-check"></i> {{ submitLoading ? '提交中...' : '提交审核' }}
        </el-button>
        <el-button 
          v-if="!isReadOnlyMode && currentExaminee"
          type="warning" 
          size="medium" 
          @click="saveDraft" 
          plain
        >
          <i class="el-icon-document-copy"></i> 保存草稿
        </el-button>
        <span v-if="isReadOnlyMode" class="readonly-text">
          <i class="el-icon-view"></i> 当前为只读模式，仅供查看
        </span>
        <el-button size="medium" @click="handleSaveAndBack">
          <i class="el-icon-back"></i> 返回
        </el-button>
      </div>
      </section>

      <!-- 右侧：评分/导航区域 -->
      <div class="right-panel">
        <!-- 得分区域（仅评分审核模式显示） -->
        <template v-if="!isSimplifiedAudit">
          <div class="score-summary">
            <span class="label">原始得分</span>
            <span class="score original">{{ originalScore }}</span>
          </div>
          
          <div class="score-summary review-score">
            <span class="label">审核得分</span>
            <span class="score" :class="{ changed: reviewScoreChanged }">{{ reviewScore }}</span>
          </div>
          
          <div class="level-display" v-if="scoreLevel">
            <span class="level-label">成绩</span>
            <span class="level-value" :style="{ color: scoreLevel.color }">{{ scoreLevel.name }}</span>
          </div>
        </template>
        
        <!-- 简化审核模式标题 -->
        <div v-else class="simplified-title">
          <span class="label">题目导航</span>
        </div>
        
        <!-- 搜索框 -->
        <div class="nav-search">
          <el-input
            v-model="navSearchKeyword"
            placeholder="搜索题目..."
            size="mini"
            prefix-icon="el-icon-search"
            clearable
          />
        </div>

        <!-- 题目导航列表 -->
        <div class="question-nav" :class="{ 'nav-list-mode': true }">
          <!-- 参考资料导航（仅考试模式显示） -->
          <template v-if="!isSimplifiedAudit && crossPhaseGroups.length > 0">
            <template v-for="group in crossPhaseGroups">
              <div :key="'nav-phase-' + group.phaseNo" class="nav-phase-title"
                   v-if="filteredRefItems(group).length > 0"
                   @click="$set(navCollapseMap, group.phaseNo, !navCollapseMap[group.phaseNo])">
                <i class="el-icon-arrow-right nav-collapse-arrow" :class="{ expanded: !navCollapseMap[group.phaseNo] }"></i>
                <span class="nav-phase-badge">{{ group.phaseNo }}</span>
                <span>{{ group.phaseName }}</span>
                <span class="nav-phase-count">{{ filteredRefItems(group).length }}题</span>
              </div>
              <template v-if="!navCollapseMap[group.phaseNo]">
              <div
                v-for="item in filteredRefItems(group)"
                :key="'nav-ref-' + group.phaseNo + '-' + item.itemKey"
                class="nav-item nav-list-item nav-ref-item"
                @click="scrollToRefItem(group.phaseNo, item.itemKey)"
                :title="stripHtml(item.label)"
              >
                <span class="nav-index">{{ item.serialNo }}.</span>
                <span class="nav-title">{{ truncateTitle(stripHtml(item.label), 10) }}</span>
                <span class="nav-ref-tag">参考</span>
              </div>
              </template>
            </template>
            <!-- 当前阶段分隔线 -->
            <div class="nav-section-divider" v-if="filteredAuditQuestions.length > 0">
              <span>当前审核</span>
            </div>
          </template>

          <!-- 当前阶段审核题导航 -->
          <div
            v-for="(q, i) in filteredAuditQuestions"
            :key="q.itemKey"
            class="nav-item nav-list-item"
            :class="getNavItemClass(q)"
            @click="scrollToQuestion(q._originalIndex)"
            :title="stripHtml(q.label)"
          >
            <span class="nav-index">{{ q.serialNo || (q._originalIndex + 1) }}.</span>
            <span class="nav-title">{{ truncateTitle(stripHtml(q.label), 10) }}</span>
            <span class="nav-status" v-if="reviewMap[q.itemKey]">
              <template v-if="isSimplifiedAudit">
                <span v-if="reviewMap[q.itemKey] === 'SCORE'" class="nav-pass">通过</span>
                <span v-else-if="reviewMap[q.itemKey] === 'NO_SCORE'" class="nav-fail">不通过</span>
              </template>
              <template v-else>
                <span v-if="reviewMap[q.itemKey] === 'SCORE'" class="nav-pass">得分</span>
                <span v-else-if="reviewMap[q.itemKey] === 'NO_SCORE'" class="nav-fail">失分</span>
                <span v-else-if="reviewMap[q.itemKey] === 'NOT_APPLICABLE'" style="color:#909399;font-size:11px;">不涉及</span>
              </template>
            </span>
          </div>
        </div>
        
        <!-- 切换按钮 -->
        <div class="nav-buttons">
          <el-button size="small" :disabled="currentIndex <= 0" @click="prevExaminee">上一份</el-button>
          <el-button size="small" :disabled="currentIndex >= filteredExamineeList.length - 1" @click="nextExaminee">下一份</el-button>
        </div>
      </div>
    </div>
    

    
    <!-- 悬浮媒体播放器 -->
    <FloatingMediaPlayer
      :visible.sync="floatingMedia.visible"
      :type="floatingMedia.type"
      :src="floatingMedia.src"
      :title="floatingMedia.title"
      :start-time="floatingMedia.startTime || 0"
      @close="closeFloatingMedia"
    />
  </div>
</template>

<script>
import AudioPlayer from './components/AudioPlayer.vue'
import FloatingMediaPlayer from './components/FloatingMediaPlayer.vue'
import { parseAgreementAnswer as _parseAgreement, renderPdfToContainer } from '@/utils/agreementPdf'
import { evaluateLogic } from '../../../survey/views/fill/LogicEngine'

export default {
  name: 'ExamGrading',
  components: {
    AudioPlayer,
    FloatingMediaPlayer
  },
  props: {
    formKey: {
      type: String,
      required: true
    },
    projectType: {
      type: Number,
      default: 0
    },
    // 从数据页面选中进入时，指定默认选中的考生
    initialAnswerKey: {
      type: String,
      default: null
    },
    // #21 阶段号（1/2/3/4），0表示非分阶段审核
    phase: {
      type: Number,
      default: 0
    },
    // 是否考试问卷（需多级审核）
    examForm: {
      type: Number,
      default: 0
    },
    waveId: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      examineeList: [],
      currentExaminee: null,
      currentIndex: -1,
      questionList: [],
      answerMap: {},        // itemKey -> 用户原始答案
      reviewAnswerMap: {},  // itemKey -> 审核人修改后的答案（当前审核级别）
      review1AnswerMap: {}, // itemKey -> 一审修改后的答案（二审/三审时使用）
      review2AnswerMap: {}, // itemKey -> 二审修改后的答案（三审时使用）
      reviewMap: {},        // itemKey -> SCORE/NO_SCORE/NOT_APPLICABLE
      preloadedReviewKeys: new Set(),  // 上一轮继承的审核结果的 itemKey（用于驳回重审时锁定）
      remarkMap: {},        // itemKey -> remark text (当前输入)
      remark1Map: {},       // itemKey -> 一审失分备注
      remark2Map: {},       // itemKey -> 二审失分备注
      reviewResult1Map: {}, // itemKey -> 一审审核结果（SCORE/NO_SCORE/NOT_APPLICABLE）
      reviewResult2Map: {}, // itemKey -> 二审审核结果（SCORE/NO_SCORE/NOT_APPLICABLE）
      touchedQuestions: {}, // itemKey -> true，用户手动操作过的题目，不再显示蓝色原答案标记
      reviewLevel: 1,       // 当前审核级别 1/2/3
      statusFilter: '',     // 状态筛选
      loading: false,
      submitLoading: false, // 提交审核中
      detailLoading: false, // 答题详情加载中
      updateCounter: 0,     // 用于强制计算属性重新计算
      answerCache: {},      // answerKey -> { answers: [], reviewDetail: {} }
      preloadQueue: [],     // 预加载队列
      otherTextMap: {},     // itemKey -> 其他选项的文本输入
      specialRemarkMap: {}, // itemKey -> 神客特殊情况说明
      reviewSpecialRemarkMap: {}, // itemKey -> 当前审核级别的特殊情况说明
      specialRemark1Map: {}, // itemKey -> 一审特殊情况说明
      specialRemark2Map: {}, // itemKey -> 二审特殊情况说明
      specialRemark3Map: {},  // itemKey -> 三审特殊情况说明
      formEnableSpecialRemark: true, // 表单级别的特殊说明开关（默认开启，审核端始终显示）
      // === 跨阶段合并展示 ===
      crossPhaseGroups: [], // 其他阶段的参考资料 [{phaseNo, phaseName, items: [...]}]
      crossPhaseLoading: false,
      refPdfStates: {}, // 参考资料 PDF 渲染状态 { 'phaseNo_itemKey': 'loading'|'done'|'error' }
      // === 悬浮媒体播放器 ===
      floatingMedia: { visible: false, type: 'image', src: '', title: '' },
      // === 导航搜索 ===
      navSearchKeyword: '',
      navCollapseMap: {}, // phaseNo -> boolean（折叠状态）
      gradingPdfStates: {}, // PDF渲染状态
      logicRules: [],
      logicHiddenSet: new Set()
    }
  },
  computed: {
    totalCount() {
      return this.examineeList.length
    },
    gradedCount() {
      return this.examineeList.filter(e => e.auditStatus >= this.reviewLevel).length
    },
    filteredExamineeList() {
      if (this.statusFilter === '' || this.statusFilter === null) {
        return this.examineeList
      }
      return this.examineeList.filter(e => e.auditStatus === this.statusFilter)
    },
    // 审核页题目列表：对齐答题端，只按 itemKey 过滤隐藏题目
    filteredQuestionList() {
      const hidden = this.logicHiddenSet instanceof Set ? this.logicHiddenSet : new Set()
      return (this.questionList || []).filter(q => !hidden.has(q.itemKey))
    },
    // 搜索过滤后的审核题导航列表（带原始索引）
    filteredAuditQuestions() {
      const keyword = (this.navSearchKeyword || '').trim().toLowerCase()
      const list = this.filteredQuestionList
      const result = []
      // 状态关键词映射
      const statusKeywords = {
        '得分': 'SCORE', '通过': 'SCORE',
        '失分': 'NO_SCORE', '不得分': 'NO_SCORE', '不通过': 'NO_SCORE',
        '不涉及': 'NOT_APPLICABLE'
      }
      // 检查搜索词是否是状态关键词
      const matchedStatus = keyword ? statusKeywords[keyword] : null
      for (let i = 0; i < list.length; i++) {
        const q = list[i]
        if (keyword) {
          if (matchedStatus) {
            // 按审核状态筛选
            if (this.reviewMap[q.itemKey] !== matchedStatus) continue
          } else {
            // 按题目标题/序号搜索
            if (!this.stripHtml(q.label).toLowerCase().includes(keyword)
                && !(q.serialNo && q.serialNo.toLowerCase().includes(keyword))) {
              continue
            }
          }
        }
        result.push({ ...q, _originalIndex: i })
      }
      return result
    },
    // 原始得分（考生答题后系统自动计算的）
    originalScore() {
      if (this.currentExaminee && this.currentExaminee.totalScore !== undefined && this.currentExaminee.totalScore !== null) {
        return parseFloat(this.currentExaminee.totalScore).toFixed(1)
      }
      return '0.0'
    },
    // 审核得分（根据当前评审结果实时计算）
    reviewScore() {
      // 依赖 updateCounter 确保响应式更新
      const _trigger = this.updateCounter
      let sum = 0
      for (const q of this.filteredQuestionList) {
        // 跳过未开启评分的题目
        if (!q.enableScoring) continue
        const result = this.reviewMap[q.itemKey]
        if (result === 'SCORE') {
          if (q.scoringMethod === 'perOption' && this.hasAllOptionsWithScore(q)) {
            // perOption 模式 + 得分 = 满分
            sum += (q.maxScore || 0)
          } else {
            // 正确答案模式或其他模式，使用题目满分
            sum += (q.maxScore || 0)
          }
        } else if (result === 'NO_SCORE') {
          // 对于 perOption 模式，不得分 = 按选中选项的分值计算扣减后的分数
          if (q.scoringMethod === 'perOption' && this.hasAllOptionsWithScore(q)) {
            const reviewAnswer = this.reviewAnswerMap[q.itemKey]
            if (reviewAnswer) {
              // 解析选中的值
              let selectedValues = []
              try {
                const parsed = JSON.parse(reviewAnswer)
                if (Array.isArray(parsed)) {
                  selectedValues = parsed.map(String)
                } else {
                  selectedValues = [String(parsed)]
                }
              } catch {
                selectedValues = [String(reviewAnswer)]
              }
              
              // 计算正分之和与负分之和
              let positiveSum = 0
              let negativeSum = 0
              selectedValues.forEach(val => {
                let opt = q.options.find(o => String(o.value) === val)
                if (!opt) {
                  opt = q.options.find(o => this.stripHtml(o.value) === val || this.stripHtml(o.label) === val)
                }
                if (opt && opt.score) {
                  if (opt.score > 0) {
                    positiveSum += opt.score
                  } else {
                    negativeSum += opt.score
                  }
                }
              })

              // 确定基础分和计算最终得分
              const fullScore = q.maxScore || 0
              let baseScore = 0
              if (positiveSum === 0 && negativeSum < 0) {
                baseScore = fullScore  // 只选负分项时，从满分扣减
              }

              // 公式：Final = min(Total, Base + Pos) + Neg
              let calculated = baseScore + positiveSum
              if (calculated > fullScore) calculated = fullScore
              let earnedScore = calculated + negativeSum
              if (earnedScore < 0) earnedScore = 0

              sum += earnedScore
            }
            // 没有选项答案时，不得分 = 0
          }
          // 非 perOption 模式，不得分 = 0（不加分）
        }
      }
      return sum.toFixed(1)
    },
    // 审核得分是否与原始得分不同
    reviewScoreChanged() {
      return this.reviewScore !== this.originalScore && this.reviewScore !== '0.0'
    },
    scoreLevel() {
      // 使用审核得分来计算等级（如果有审核结果），否则使用原始得分
      const score = this.reviewScoreChanged ? parseFloat(this.reviewScore) : parseFloat(this.originalScore)
      if (score >= 90) return { name: '优秀', color: '#67C23A' }
      if (score >= 75) return { name: '良好', color: '#409EFF' }
      if (score >= 60) return { name: '及格', color: '#E6A23C' }
      return { name: '不及格', color: '#F56C6C' }
    },
    // 当前级别文本
    reviewLevelText() {
      const texts = { 1: '一', 2: '二', 3: '三' }
      return texts[this.reviewLevel] || ''
    },
    // 当前级别是否可编辑
    canEditCurrentLevel() {
      if (!this.currentExaminee) return false
      const status = this.currentExaminee.auditStatus || 0
      // 管理员始终可编辑（允许重新审核已完成的答卷）
      if (this.isAdminUser) return true
      // 有审核级别切换（examForm===1）时，使用reviewLevel逻辑
      if (this.examForm === 1) {
        return this.reviewLevel === status + 1 || (status === 0 && this.reviewLevel === 1)
      }
      // 纯分阶段模式（无审核级别）：未审核(0)或驳回重审(-1)都可编辑
      if (this.phase > 0) return status === 0 || status === -1
      // 考试模式
      return this.reviewLevel === status + 1 || (status === 0 && this.reviewLevel === 1)
    },
    // 是否是只读模式
    isReadOnlyMode() {
      if (!this.currentExaminee) return false
      // 管理员永不只读（允许重新审核已完成的答卷）
      if (this.isAdminUser) return false
      const status = this.currentExaminee.auditStatus || 0
      // 有审核级别切换（examForm===1）时，按reviewLevel判断
      if (this.examForm === 1) {
        const result = this.reviewLevel <= status
        return result
      }
      // 纯分阶段模式（无审核级别）：已完成(>=1)才只读；驳回重审(-1)不只读
      if (this.phase > 0) return status >= 1
      // 考试模式
      const result = this.reviewLevel <= status
      return result
    },
    // 是否需要先完成上级审核（选择的级别比当前状态高出超过1级）
    needPreviousLevel() {
      if (!this.currentExaminee) return false
      const status = this.currentExaminee.auditStatus || 0
      // 如果当前选择的级别比已完成的状态高出超过1级，需要先完成上级
      return this.reviewLevel > status + 1
    },
    // 需要完成哪一级审核的文本
    previousLevelText() {
      const texts = { 1: '一', 2: '二', 3: '三' }
      const status = this.currentExaminee?.auditStatus || 0
      return texts[status + 1] || '一'
    },
    // #21 是否为简化审核模式（阶段1/2/4只需通过/不通过）
    isSimplifiedAudit() {
      return this.phase > 0 && this.phase !== 3
    },
    // 当前用户的审核权限列表（来自 jkd_roles_tb.check_permission）
    allowedReviewLevels() {
      const info = this.$store.getters.userInfo || {}
      const perm = info.check_permission
      // check_permission 可能是数组（已在 api/index.js 中解析）或逗号分隔字符串
      if (Array.isArray(perm) && perm.length > 0) {
        return perm.map(Number).filter(n => n >= 1 && n <= 3)
      }
      if (typeof perm === 'string' && perm.trim()) {
        return perm.split(',').map(Number).filter(n => n >= 1 && n <= 3)
      }
      // 没有配置 check_permission → 默认全部开放（管理员等角色）
      return [1, 2, 3]
    },
    // 是否为管理员/超级管理员（不受审核权限限制）
    isAdminUser() {
      const info = this.$store.getters.userInfo || {}
      const roleName = (info.roles && info.roles.name) || info.role_name || info.roleName || ''
      return ['管理员', '超级管理员', 'admin', 'superadmin'].includes(roleName)
    },
    // 是否显示原答案标记（蓝色勾+"原答案"标签）
    // 一审编辑模式不显示，让审核员从空白开始；二审三审及只读模式正常显示
    shouldShowOriginalMarks() {
      if (this.isSimplifiedAudit) return true  // 阶段1/2/4始终显示
      if (this.isReadOnlyMode) return true     // 只读模式显示
      // 多级审核编辑模式：一审不显示，二审三审显示
      return this.reviewLevel > 1
    }
  },
  created() {
    // 先加载题目，再加载答卷并选中考生，避免逻辑计算时 questionList 尚未就绪
    this.initPageData()
    // 根据用户审核权限自动选择可用的最低级别
    if (!this.isAdminUser && this.allowedReviewLevels.length > 0) {
      const minLevel = Math.min(...this.allowedReviewLevels)
      if (minLevel !== this.reviewLevel) {
        this.reviewLevel = minLevel
      }
    }
  },
  mounted() {
    // 调整父容器样式，让阅卷页面填满整个可视区域（减去顶部导航60px）
    const dataPage = this.$el.closest('.data-page')
    if (dataPage) {
      this._originalHeight = dataPage.style.height
      this._originalBackground = dataPage.style.background
      dataPage.style.height = 'calc(100vh - 60px)'
      dataPage.style.background = '#f5f7fa'
    }
  },
  beforeDestroy() {
    // 恢复父容器原始样式
    const dataPage = this.$el.closest('.data-page')
    if (dataPage) {
      if (this._originalHeight !== undefined) {
        dataPage.style.height = this._originalHeight
      }
      if (this._originalBackground !== undefined) {
        dataPage.style.background = this._originalBackground
      }
    }
  },
  methods: {
    async initPageData() {
      await this.loadQuestions()
      await this.loadExamineeList()
    },

    async loadExamineeList() {
      try {
        this.loading = true
        const { getAnswerPage } = await import('index/api/project/form')
        const params = { formKey: this.formKey, page: 1, size: 500 }
        if (this.waveId) params.waveId = this.waveId
        const res = await getAnswerPage(params)
        const data = res.data?.data || res.data || {}
        const records = data.records || data.list || []
        
        this.examineeList = records.map(r => ({
          answerKey: r.answerKey,
          submitTime: r.submitTime,
          durationSeconds: r.durationSeconds,
          totalScore: r.totalScore,
          reviewedScore: r.reviewedScore,
          auditStatus: r.auditStatus || 0,
          dealerCode: r.dealerCode || r.DealerCode || null,
          uniqueCode: r.uniqueCode || null,
          dealerName: r.dealerName || r.DealerName || ''
          // 注意：不再在列表加载时存储 answers，改为选中时加载
        }))
        
        // 如果后端返回了 answers，缓存起来
        records.forEach(r => {
          if (r.answers && r.answers.length > 0) {
            this.answerCache[r.answerKey] = { answers: r.answers }
          }
        })
        
        this.loading = false
        
        if (this.examineeList.length > 0) {
          if (this.initialAnswerKey) {
            const targetExaminee = this.examineeList.find(e => e.answerKey === this.initialAnswerKey)
            if (targetExaminee) {
              await this.selectExaminee(targetExaminee)
            } else {
              await this.selectExaminee(this.examineeList[0])
            }
          } else {
            await this.selectExaminee(this.examineeList[0])
          }
        }
      } catch (e) {
        this.loading = false
        console.error('[ExamGrading] Load examinee list failed:', e)
      }
    },
    
    async loadQuestions() {
      try {
        const { getFormItems } = await import('index/api/project/form')
        const res = await getFormItems(this.formKey)
        const items = res.data?.data || res.data || []
        
        
        this.questionList = items.filter(item => {
          // 兼容 itemType 和 item_type 两种字段名
          const type = item.itemType || item.item_type
          return !this.isPaginationItem(type)
        }).map(item => {
          // 兼容 itemType 和 item_type 两种字段名
          const itemType = item.itemType || item.item_type
          
          // 解析 config_json 获取配置
          let config = {}
          try {
            const configStr = item.configJson || item.config_json || '{}'
            config = JSON.parse(configStr)
          } catch (e) {
           
          }
          
          // 获取选项：优先使用 options，兼容不同字段名
          // 合并 config_json.__slot__.options 中的 notApplicable、score 等属性
          const rawOptions = item.options || item.optionsRaw || []
          const slotOptions = config.__slot__?.options || []
          const options = rawOptions.map((opt, idx) => {
            const slotOpt = slotOptions.find(so => String(so.value) === String(opt.value)) || slotOptions[idx]
            if (slotOpt) {
              return { ...opt, ...(slotOpt.score !== undefined ? { score: slotOpt.score } : {}), ...(slotOpt.notApplicable !== undefined ? { notApplicable: slotOpt.notApplicable } : {}) }
            }
            return opt
          })
          
          // 尝试从多个来源获取分数（兼容不同配置格式）
          let scoreValue = item.score || item.maxScore || config.score || config.__config__?.score || 0
          
          // 如果没有直接的分数字段，尝试从选项中获取最大分数
          if (!scoreValue && options.length > 0) {
            const optionScores = options.map(opt => opt.score || 0)
            scoreValue = Math.max(...optionScores)
          }
          
          // 直接使用题目设置的分数，不设置默认值
          
          // 获取正确答案：优先从 config_json 中获取
          const correctAnswer = item.correctAnswer || config.correctAnswer || null
          const cfg = config || {}
          const cfgConfig = cfg.__config__ || {}
          const cfgSlot = cfg.__slot__ || {}
          const pick = (...vals) => {
            for (const v of vals) {
              if (v === null || v === undefined) continue
              const s = String(v).trim()
              if (s && s !== 'null' && s !== 'undefined') return s
            }
            return ''
          }

          const mappedQuestion = {
            itemKey: item.itemKey || item.item_key,
            itemType: itemType,
            typeName: this.getTypeName(itemType),
            label: item.label || item.title,
            serialNo: pick(item.serialNo, item.serial_no, cfgConfig.serialNo, cfgConfig.serial_no, cfg.serialNo, cfg.serial_no, cfgSlot.serialNo, cfgSlot.serial_no),
            options: options,
            correctAnswer: correctAnswer,
            maxScore: scoreValue,
            configJson: item.configJson || item.config_json || '{}',
            description: item.description || cfgConfig.tipContent || cfg.tipContent || cfgConfig.description || '',
            tipContent: item.tipContent || item.tip_content || cfgConfig.tipContent || cfg.tipContent || '',
            scoringMethod: cfg.scoringMethod || 'single',
            enableScoring: cfg.enableScoring,
            enableSpecialRemark: cfg.enableSpecialRemark,
            dataSourceType: pick(item.dataSourceType, item.data_source_type, item.datasourceType, item.datasource_type, cfg.dataSourceType, cfg.data_source_type, cfg.datasourceType, cfgConfig.dataSourceType, cfgConfig.data_source_type, cfgSlot.dataSourceType, cfgSlot.data_source_type),
            sourceFormKey: pick(item.sourceFormKey, item.source_form_key, item.sourceformkey, cfg.sourceFormKey, cfg.source_form_key, cfg.sourceformkey, cfgConfig.sourceFormKey, cfgConfig.source_form_key, cfgSlot.sourceFormKey, cfgSlot.source_form_key),
            sourceItemKey: pick(item.sourceItemKey, item.source_item_key, item.sourceitemkey, cfg.sourceItemKey, cfg.source_item_key, cfg.sourceitemkey, cfgConfig.sourceItemKey, cfgConfig.source_item_key, cfgSlot.sourceItemKey, cfgSlot.source_item_key),
            // 关系ID（后端关系表返回）
            relationIds: Array.isArray(item.relationIds) ? item.relationIds : (Array.isArray(item.relation_ids) ? item.relation_ids : (item.relationId ? [item.relationId] : (item.relation_id ? [item.relation_id] : []))),
            isReferenced: item.isReferenced === true || item.is_referenced === true,
            isLoopbackReference: item.isLoopbackReference === true || item.is_loopback_reference === true,
            isLoopbackSource: item.isLoopbackSource === true || item.is_loopback_source === true,
            // 题目属性键（回圈关联有时引用 __vModel__）
            vModel: pick(item.__vModel__, item.vModel, item.v_model, cfg.__vModel__, cfg.vModel, cfg.v_model, cfgConfig.__vModel__, cfgConfig.vModel, cfgSlot.__vModel__, cfgSlot.vModel),
            // 回圈选项映射（可选）：key=源题选项value，value=目标题选项value
            // 兼容多种历史字段名（含 __slot__）
            optionMapping: item.optionMapping || item.sourceOptionMapping || item.loopbackOptionMapping || config.optionMapping || config.sourceOptionMapping || config.loopbackOptionMapping || config.__config__?.optionMapping || config.__config__?.sourceOptionMapping || config.__config__?.loopbackOptionMapping || config.__slot__?.optionMapping || config.__slot__?.sourceOptionMapping || config.__slot__?.loopbackOptionMapping || null,
            exclusive: config.exclusive || config.__config__?.exclusive || false,
            exclusiveOptions: config.exclusiveOptions || config.__config__?.exclusiveOptions || [],
            exclusiveGroups: config.exclusiveGroups || config.__config__?.exclusiveGroups || []
          }


          return mappedQuestion
        })
      } catch (e) {
        console.error('[ExamGrading] Load questions failed:', e)
      }
    },
    
    async selectExaminee(examinee) {
      this.currentExaminee = examinee
      this.currentIndex = this.filteredExamineeList.findIndex(e => e.answerKey === examinee.answerKey)

      // 先加载逻辑规则，确保审核端题目可见性与答题端一致
      await this.ensureLogicRulesLoaded()
      
      // 重置答案映射
      this.answerMap = {}
      this.reviewAnswerMap = {}
      this.reviewMap = {}
      this.preloadedReviewKeys = new Set()
      this.logicHiddenSet = new Set()
      this.remarkMap = {}
      this.review1AnswerMap = {}
      this.review2AnswerMap = {}
      this.reviewResult1Map = {}
      this.reviewResult2Map = {}
      this.touchedQuestions = {}
      this.otherTextMap = {}
      this.specialRemarkMap = {}
      this.reviewSpecialRemarkMap = {}
      this.specialRemark1Map = {}
      this.specialRemark2Map = {}
      this.specialRemark3Map = {}
      
      const answerKey = examinee.answerKey
      
      // 从缓存加载答题详情，或发起请求
      let answers = []
      let reviewDetail = null
      
      if (this.answerCache[answerKey]) {
        // 命中缓存
        answers = this.answerCache[answerKey].answers || []
        reviewDetail = this.answerCache[answerKey].reviewDetail
      } else {
        // 需要加载
        this.detailLoading = true
        try {
          const { getAnswerDetail } = await import('index/api/project/form')
          const res = await getAnswerDetail(answerKey)
          const data = res.data?.data || res.data || {}
          answers = data.answers || []
          // 缓存答题详情
          this.answerCache[answerKey] = { answers }
        } catch (e) {
          console.warn('[ExamGrading] Load answer detail failed:', e)
        }
        this.detailLoading = false
      }
      
      // 构建答案映射（只记录原答案，不自动填充审核答案）
      answers.forEach(ans => {
        const key = ans.itemKey || ans.item_key
        const value = ans.value || ans.answer
        this.$set(this.answerMap, key, value)
        // 保存"其他"选项的文本输入
        if (ans.other_text || ans.otherText) {
          this.$set(this.otherTextMap, key, ans.other_text || ans.otherText)
        }
      })

      // 按答题端逻辑引擎计算隐藏题（审核端与答题端保持一致）
      this.applyLogicVisibility()
      
      // 加载审核详情（带缓存）
      if (reviewDetail) {
        this.applyReviewDetail(reviewDetail)
      } else {
        try {
          const { getReviewDetail } = await import('index/api/project/form')
          const res = await getReviewDetail(answerKey)
          const data = res.data?.data || res.data || {}
          // 缓存审核详情
          if (this.answerCache[answerKey]) {
            this.answerCache[answerKey].reviewDetail = data
          }
          this.applyReviewDetail(data)
        } catch (e) {
          console.error('[ExamGrading] Load review detail failed:', e)
        }
      }

      // 结合审核答案再次计算显示逻辑（保证审核端按实际触发条件显示）
      this.applyLogicVisibility()

      // === 加载跨阶段参考资料（严格同店：优先 uniqueCode） ===
      const crossPhaseCode = examinee.uniqueCode || examinee.dealerCode
      console.log('[ExamGrading] 跨阶段参考: uniqueCode=', examinee.uniqueCode, 'dealerCode=', examinee.dealerCode, '→ crossPhaseCode=', crossPhaseCode)
      if (crossPhaseCode) {
        await this.loadCrossPhaseData(crossPhaseCode)
        console.log('[ExamGrading] 跨阶段参考加载完成, groups=', this.crossPhaseGroups.length)
      } else {
        console.log('[ExamGrading] 跨阶段参考: 无 dealerCode，跳过加载')
        this.crossPhaseGroups = []
      }

      // 将“同店跨阶段答案”按回圈关系预填到当前审核答案
      if (this.phase !== 3) {
        this.applyCrossPhaseLoopbackDefaults()
      }
      // 反向回圈：其他阶段的题目引用了当前阶段的题目时，拉取其他阶段的答案
      this.applyReverseCrossPhaseLoopback()
      

      // 初始化未设置的题目
      this.questionList.forEach(q => {
        if (!(q.itemKey in this.reviewMap) || !this.reviewMap[q.itemKey]) {
          // 所有审核模式均不自动评分，等待审核员手动选择
          // - 简化审核模式（阶段1/2/4）：通过/不通过
          // - 多级审核模式（phase=0/phase=3/examForm=1）：得分/不得分/不涉及
        }
        if (!(q.itemKey in this.remarkMap)) {
          this.$set(this.remarkMap, q.itemKey, '')
        }
      })
      
      // 恢复草稿（如果有）
      this.restoreDraft()
      
      // 预加载相邻的项（后台静默加载）
      this.preloadAdjacent()
      
      // 渲染廉政协议 PDF
      this.$nextTick(() => this.renderGradingPdfs())
    },
    
    async ensureLogicRulesLoaded() {
      if (Array.isArray(this.logicRules) && this.logicRules.length > 0) return

      const isValidLogicRule = (r) => {
        if (!r || typeof r !== 'object') return false
        const logicType = r.logicType || r.logic_type
        const sourceItemKey = r.sourceItemKey || r.source_item_key
        const targetItemKey = r.targetItemKey || r.target_item_key
        return !!logicType && (!!sourceItemKey || !!targetItemKey)
      }

      let loadedRules = []

      // 1) 优先走逻辑接口
      try {
        const { getFormLogicRequest } = await import('index/api/project/form')
        const res = await getFormLogicRequest(this.formKey)
        const data = res.data?.data || res.data || []
        const candidate = Array.isArray(data) ? data : []
        loadedRules = candidate.filter(isValidLogicRule)

        if (candidate.length > 0 && loadedRules.length === 0) {
          console.warn('[ExamGrading] Logic API returned non-rule payload, fallback to form detail logic.')
        }
      } catch (e) {
        console.warn('[ExamGrading] Load logic rules from logic API failed:', e)
      }

      // 2) 兜底：与答题端一致，从表单详情里读取 logic
      if (!loadedRules || loadedRules.length === 0) {
        try {
          const { getProjectRequest } = await import('index/api/project/form')
          const res = await getProjectRequest(this.formKey)
          const formData = res.data?.data || res.data || {}
          const detailLogic = formData.logic
          const candidate = Array.isArray(detailLogic) ? detailLogic : []
          loadedRules = candidate.filter(isValidLogicRule)
        } catch (e) {
          console.warn('[ExamGrading] Load logic rules from form detail failed:', e)
        }
      }

      this.logicRules = Array.isArray(loadedRules) ? loadedRules : []
    },

    getEffectiveAnswerMap() {
      const normalize = (v) => {
        if (v === null || v === undefined) return ''
        const s = String(v).trim()
        return (!s || s === 'undefined' || s === 'null') ? '' : s
      }

      const parseLogicValue = (value) => {
        if (typeof value !== 'string') return value
        const s = value.trim()
        if (!s) return value
        if ((s.startsWith('"') && s.endsWith('"')) ||
            (s.startsWith('[') && s.endsWith(']')) ||
            (s.startsWith('{') && s.endsWith('}'))) {
          try {
            return JSON.parse(s)
          } catch (e) {
            return value
          }
        }
        return value
      }

      // 1) 先构建别名 -> itemKey 映射（UUID）
      const aliasToItemKey = new Map()
      ;(this.questionList || []).forEach(q => {
        const itemKey = normalize(q.itemKey)
        if (!itemKey) return

        const aliases = [itemKey, normalize(q.serialNo), normalize(q.vModel)]
        try {
          const cfg = JSON.parse(q.configJson || '{}')
          aliases.push(
            normalize(cfg.__vModel__ || cfg.vModel || cfg.v_model),
            normalize(cfg.__config__?.__vModel__ || cfg.__config__?.vModel || cfg.__config__?.v_model),
            normalize(cfg.__slot__?.__vModel__ || cfg.__slot__?.vModel || cfg.__slot__?.v_model)
          )
        } catch (e) {}

        ;[...new Set(aliases.filter(Boolean))].forEach(a => {
          if (!aliasToItemKey.has(a)) aliasToItemKey.set(a, itemKey)
        })
      })

      const merged = {}

      // 2) 原答案：无论 key 是 UUID/serialNo/vModel，统一回填到 UUID
      Object.keys(this.answerMap || {}).forEach(rawKey => {
        const canonicalKey = aliasToItemKey.get(normalize(rawKey)) || normalize(rawKey)
        const value = parseLogicValue(this.answerMap[rawKey])
        if (canonicalKey) merged[canonicalKey] = value
      })

      // 3) 审核端显隐只按神秘客原始答案计算：不使用 reviewAnswerMap 覆盖

      // 4) 选择题：若答案是选项文案，映射成 option.value（与规则 conditionValue 对齐）
      ;(this.questionList || []).forEach(q => {
        const itemKey = normalize(q.itemKey)
        if (!itemKey) return
        let value = merged[itemKey]
        if (value === null || value === undefined || String(value).trim() === '') return

        if (this.isChoiceQuestion(q.itemType) && Array.isArray(q.options) && q.options.length > 0) {
          const labelToValue = new Map()
          q.options.forEach(opt => {
            const plainLabel = normalize(this.stripHtml(opt.label || ''))
            if (plainLabel) labelToValue.set(plainLabel, opt.value)
          })

          const mapOne = (v) => {
            const nv = normalize(v)
            return (nv && labelToValue.has(nv)) ? labelToValue.get(nv) : v
          }

          value = Array.isArray(value) ? value.map(mapOne) : mapOne(value)
          merged[itemKey] = value
        }

        // 5) 再把同一个值铺到别名键（兼容规则 sourceItemKey 写法）
        const aliases = [normalize(q.serialNo), normalize(q.vModel)]
        try {
          const cfg = JSON.parse(q.configJson || '{}')
          aliases.push(
            normalize(cfg.__vModel__ || cfg.vModel || cfg.v_model),
            normalize(cfg.__config__?.__vModel__ || cfg.__config__?.vModel || cfg.__config__?.v_model),
            normalize(cfg.__slot__?.__vModel__ || cfg.__slot__?.vModel || cfg.__slot__?.v_model)
          )
        } catch (e) {}

        ;[...new Set(aliases.filter(Boolean))].forEach(alias => {
          merged[alias] = value
        })
      })

      return merged
    },

    applyLogicVisibility() {
      try {
        const effectiveAnswers = this.getEffectiveAnswerMap()

        // 规范化规则：补齐 formKey，避免 sourceFormKey 判定为 external 导致条件失效
        const normalizedRules = (this.logicRules || []).map(rule => {
          const r = { ...rule }
          if (!r.formKey && !r.form_key) {
            r.formKey = this.formKey
          }

          const actionConfig = r.actionConfig || r.action_config
          if (actionConfig) {
            try {
              const cfg = typeof actionConfig === 'string' ? JSON.parse(actionConfig) : { ...actionConfig }
              if (cfg?.conditionGroup?.conditions && Array.isArray(cfg.conditionGroup.conditions)) {
                cfg.conditionGroup.conditions = cfg.conditionGroup.conditions.map(c => {
                  const cc = { ...c }
                  if (!cc.sourceFormKey) cc.sourceFormKey = this.formKey
                  return cc
                })
                r.actionConfig = JSON.stringify(cfg)
              }
            } catch (e) {}
          }
          return r
        })

        const result = evaluateLogic(normalizedRules, effectiveAnswers)
        const rawHidden = result?.hidden instanceof Set ? result.hidden : new Set()

        // 审核页最终过滤按 itemKey；将 serialNo/vModel 等别名映射回 itemKey
        const hiddenByItemKey = new Set()
        const normalize = (v) => {
          if (v === null || v === undefined) return ''
          const s = String(v).trim()
          return (!s || s === 'undefined' || s === 'null') ? '' : s
        }

        const aliasToItemKey = new Map()
        ;(this.questionList || []).forEach(q => {
          const itemKey = normalize(q.itemKey)
          if (!itemKey) return

          const aliases = [itemKey, normalize(q.serialNo), normalize(q.vModel)]
          try {
            const cfg = JSON.parse(q.configJson || '{}')
            aliases.push(
              normalize(cfg.__vModel__ || cfg.vModel || cfg.v_model),
              normalize(cfg.__config__?.__vModel__ || cfg.__config__?.vModel || cfg.__config__?.v_model),
              normalize(cfg.__slot__?.__vModel__ || cfg.__slot__?.vModel || cfg.__slot__?.v_model)
            )
          } catch (e) {}

          ;[...new Set(aliases.filter(Boolean))].forEach(a => {
            if (!aliasToItemKey.has(a)) aliasToItemKey.set(a, itemKey)
          })
        })

        rawHidden.forEach(h => {
          const key = normalize(h)
          if (!key) return
          const itemKey = aliasToItemKey.get(key)
          if (itemKey) hiddenByItemKey.add(itemKey)
        })

        this.logicHiddenSet = hiddenByItemKey
      } catch (e) {
        console.warn('[ExamGrading] Evaluate logic failed:', e)
        this.logicHiddenSet = new Set()
      }
    },

    // 应用审核详情数据
    applyReviewDetail(data) {
      if (!data.items) return
      
      data.items.forEach(item => {
        // 加载一审答案和结果
        if (item.reviewAnswer1) {
          this.$set(this.review1AnswerMap, item.itemKey, item.reviewAnswer1)
        }
        if (item.reviewResult1) {
          this.$set(this.reviewResult1Map, item.itemKey, item.reviewResult1)
        }
        // 加载二审答案和结果
        if (item.reviewAnswer2) {
          this.$set(this.review2AnswerMap, item.itemKey, item.reviewAnswer2)
        }
        if (item.reviewResult2) {
          this.$set(this.reviewResult2Map, item.itemKey, item.reviewResult2)
        }
        
        // 加载神客特殊说明
        if (item.specialRemark) {
          this.$set(this.specialRemarkMap, item.itemKey, item.specialRemark)
        }
        // 加载历史审核特殊说明
        if (item.specialRemark1) this.$set(this.specialRemark1Map, item.itemKey, item.specialRemark1)
        if (item.specialRemark2) this.$set(this.specialRemark2Map, item.itemKey, item.specialRemark2)
        if (item.specialRemark3) this.$set(this.specialRemark3Map, item.itemKey, item.specialRemark3)
        
        // 加载历史失分备注
        if (item.reviewRemark1) this.$set(this.remark1Map, item.itemKey, item.reviewRemark1)
        if (item.reviewRemark2) this.$set(this.remark2Map, item.itemKey, item.reviewRemark2)
        
        // 根据审核级别获取对应级别的结果、答案和备注
        // 简化审核模式（阶段1/2/4）统一按一审字段处理
        // 【修复】只预填"得分"(SCORE)的结果，"不得分"(NO_SCORE)不继承，让审核员重新判断
        let result, answer, remark, specialRemark
        if (this.isSimplifiedAudit) {
          result = item.reviewResult1
          answer = item.reviewAnswer1
          remark = item.reviewRemark1
          specialRemark = item.specialRemark1
        } else if (this.reviewLevel === 3) {
          // 优先取本级结果，否则只继承上一级"得分"的结果
          result = item.reviewResult3
          if (!result && item.reviewResult2 === 'SCORE') result = 'SCORE'
          else if (!result && !item.reviewResult2 && item.reviewResult1 === 'SCORE') result = 'SCORE'
          answer = item.reviewAnswer3 || (item.reviewResult2 === 'SCORE' ? item.reviewAnswer2 : null) || (item.reviewResult1 === 'SCORE' ? item.reviewAnswer1 : null)
          remark = item.reviewRemark3
          specialRemark = item.specialRemark3
        } else if (this.reviewLevel === 2) {
          result = item.reviewResult2
          if (!result && item.reviewResult1 === 'SCORE') result = 'SCORE'
          answer = item.reviewAnswer2 || (item.reviewResult1 === 'SCORE' ? item.reviewAnswer1 : null)
          remark = item.reviewRemark2
          specialRemark = item.specialRemark2
        } else {
          result = item.reviewResult1
          answer = item.reviewAnswer1
          remark = item.reviewRemark1
          specialRemark = item.specialRemark1
        }
        
        if (result) {
          // 多级审核模式（阶段3的一审/二审/三审）：只有该级别确实已审核（auditStatus >= reviewLevel）时才加载结果
          // 避免后端返回的自动计算结果导致题目被默认选中
          // 阶段1/2/4（简化审核）：始终预填，它们没有多级审核
          const auditStatus = this.currentExaminee?.auditStatus || 0
          if (!this.isSimplifiedAudit && auditStatus < this.reviewLevel) {
            // 阶段3多级审核：尚未审核到当前级别，不预填
          } else {
            this.$set(this.reviewMap, item.itemKey, result)
            // 记录从上一轮继承的审核结果（用于驳回重审时锁定）
            this.preloadedReviewKeys.add(item.itemKey)
          }
        }
        // 答案预填充逻辑
        if (answer && this.isReadOnlyMode) {
          // 只读模式：加载当前级别已有审核答案
          this.$set(this.reviewAnswerMap, item.itemKey, answer)
        } else if (this.isSimplifiedAudit && !this.isReadOnlyMode) {
          // 阶段1/2/4 简化审核编辑模式：已有审核结果时恢复答案
          // 确保提交后刷新不会"丢失"已选答案
          if (item.reviewResult1 && item.reviewAnswer1) {
            this.$set(this.reviewAnswerMap, item.itemKey, item.reviewAnswer1)
          }
        } else if (!this.isSimplifiedAudit && !this.isReadOnlyMode) {
          // 多级审核编辑模式：一审不预填，二审只选中一审得分的，三审只选中二审得分的
          let prevAnswer = null
          if (this.reviewLevel === 2 && item.reviewResult1 === 'SCORE') prevAnswer = item.reviewAnswer1
          else if (this.reviewLevel === 3 && item.reviewResult2 === 'SCORE') prevAnswer = item.reviewAnswer2
          // reviewLevel === 1（一审）：prevAnswer 保持 null，不预填任何答案
          if (prevAnswer) {
            this.$set(this.reviewAnswerMap, item.itemKey, prevAnswer)
          }
        }
        if (remark) {
          this.$set(this.remarkMap, item.itemKey, remark)
        }
        if (specialRemark) {
          this.$set(this.reviewSpecialRemarkMap, item.itemKey, specialRemark)
        }
      })
    },
    
    // 预加载相邻的考生详情（静默后台加载）
    async preloadAdjacent() {
      const list = this.filteredExamineeList
      const idx = this.currentIndex
      const preloadRange = 2 // 预加载前后各2个
      
      for (let offset = 1; offset <= preloadRange; offset++) {
        // 预加载后面的项
        if (idx + offset < list.length) {
          this.preloadExamineeDetail(list[idx + offset])
        }
        // 预加载前面的项
        if (idx - offset >= 0) {
          this.preloadExamineeDetail(list[idx - offset])
        }
      }
    },
    
    // 预加载单个考生的详情（静默，不显示 loading）
    async preloadExamineeDetail(examinee) {
      const answerKey = examinee.answerKey
      if (this.answerCache[answerKey]) return // 已缓存
      
      try {
        const { getAnswerDetail } = await import('index/api/project/form')
        const res = await getAnswerDetail(answerKey)
        const data = res.data?.data || res.data || {}
        this.answerCache[answerKey] = { answers: data.answers || [] }
        
        // 同时预加载审核详情
        const { getReviewDetail } = await import('index/api/project/form')
        const reviewRes = await getReviewDetail(answerKey)
        const reviewData = reviewRes.data?.data || reviewRes.data || {}
        this.answerCache[answerKey].reviewDetail = reviewData
      } catch (e) {
        // 预加载失败不影响正常使用
      }
    },
    
    // === 跨阶段数据加载 ===
    async loadCrossPhaseData(dealerCode) {
      this.crossPhaseLoading = true
      try {
        const strictDealerCode = (dealerCode || '').trim()
        if (!strictDealerCode) {
          this.crossPhaseGroups = []
          return
        }

        const { getCrossPhaseData } = await import('index/api/project/form')
        const res = await getCrossPhaseData(strictDealerCode, this.formKey)
        const data = res.data?.data || res.data || {}
        this.crossPhaseGroups = (data.phases || []).map(phase => ({
          phaseNo: phase.phaseNo,
          phaseName: phase.phaseName,
          submitTime: phase.submitTime,
          items: (phase.items || []).filter(item => {
            // 过滤分页项
            return !this.isPaginationItem(item.itemType)
          }).map(item => ({
            itemKey: item.itemKey,
            itemType: item.itemType,
            label: item.label,
            serialNo: item.serialNo,
            options: item.options || [],
            answer: item.answer // { value, otherText } 或 null
          }))
        }))
      } catch (e) {
        console.warn('[ExamGrading] Load cross-phase data failed:', e)
        this.crossPhaseGroups = []
      }
      this.crossPhaseLoading = false
      // 渲染参考资料中的廉政协议 PDF
      this.$nextTick(() => this.renderCrossPhasePdfs())
    },
    
    // 获取跨阶段题目的答案值（优先取审核答案，其次取原始答案）
    getCrossPhaseAnswerValue(item) {
      if (!item.answer) return null
      return item.answer.reviewAnswer1 || item.answer.value || null
    },



    // 将“同店跨阶段答案”按回圈关系预填到当前审核答案
    applyCrossPhaseLoopbackDefaults() {
      const normalize = (v) => {
        if (v === null || v === undefined) return ''
        const s = String(v).trim()
        return (!s || s === 'undefined' || s === 'null') ? '' : s
      }

      const parseToValues = (raw) => {
        if (raw === null || raw === undefined) return []
        if (Array.isArray(raw)) return raw.map(v => String(v))
        if (typeof raw === 'string') {
          const s = raw.trim()
          if (!s) return []
          try {
            const parsed = JSON.parse(s)
            if (Array.isArray(parsed)) return parsed.map(v => String(v))
            if (parsed !== null && parsed !== undefined) return [String(parsed)]
          } catch (e) {}
          return [s]
        }
        return [String(raw)]
      }

      const crossPhaseAnswerMap = new Map()
      ;(this.crossPhaseGroups || []).forEach(group => {
        ;(group.items || []).forEach(item => {
          const rawValue = this.getCrossPhaseAnswerValue(item)
          const v = normalize(rawValue)
          if (!v) return

          const keys = [normalize(item.itemKey), normalize(item.serialNo)]
          ;[...new Set(keys.filter(Boolean))].forEach(k => {
            if (!crossPhaseAnswerMap.has(k)) crossPhaseAnswerMap.set(k, rawValue)
          })
        })
      })

      if (crossPhaseAnswerMap.size === 0) return

      ;(this.questionList || []).forEach(targetQ => {
        if (!this.isChoiceQuestion(targetQ?.itemType)) return

        const current = this.reviewAnswerMap[targetQ.itemKey]
        if (normalize(current)) return // 用户已选，不覆盖

        const sourceKey = normalize(targetQ.sourceItemKey)
        if (!sourceKey) return

        // 【防御】sourceItemKey 不能等于自身（编辑器误配数据防护）
        if (sourceKey === normalize(targetQ.itemKey)) return

        // 【防御】必须有 sourceFormKey 且指向其他问卷才是有效回圈配置
        const sourceFormKey = normalize(targetQ.sourceFormKey)
        if (!sourceFormKey) return

        // 【BUG修复】多级审核二审/三审：上一级对该题给出 NO_SCORE 时，不从跨阶段原答案回圈
        // 保证"得分才带到下一级"的规则不被跨阶段回圈覆盖
        if (!this.isSimplifiedAudit && this.reviewLevel > 1) {
          const prevResult = this.reviewLevel === 2
            ? this.reviewResult1Map[targetQ.itemKey]
            : this.reviewResult2Map[targetQ.itemKey]
          if (prevResult === 'NO_SCORE' || prevResult === 'NOT_APPLICABLE') return
        }

        const sourceRaw = crossPhaseAnswerMap.get(sourceKey)
        if (sourceRaw === null || sourceRaw === undefined) return

        let values = parseToValues(sourceRaw)
        if (values.length === 0) return

        // 应用 optionMapping
        const mapping = this.normalizeOptionMapping(targetQ.optionMapping)
        if (mapping) {
          values = values.map(v => {
            const key = String(v)
            return Object.prototype.hasOwnProperty.call(mapping, key) ? String(mapping[key]) : key
          })
        }

        // 仅保留目标题存在的选项值
        const targetOptionValues = new Set(((targetQ.options || []).map(o => String(o.value))))
        if (targetOptionValues.size > 0) {
          values = values.filter(v => targetOptionValues.has(String(v)))
        }

        values = [...new Set(values)]
        if (values.length === 0) return

        const finalAnswer = this.isMultipleChoice(targetQ.itemType)
          ? JSON.stringify(values)
          : JSON.stringify([values[0]])

        this.$set(this.reviewAnswerMap, targetQ.itemKey, finalAnswer)
        this.updateReviewResultFromAnswer(targetQ, finalAnswer)
      })
    },

    // 反向回圈：其他阶段的题目配置了 sourceItemKey 引用当前阶段的题目
    // 时，从其他阶段拉取答案预填到当前题目
    // 场景：Phase 3 的 S1 配置了 sourceItemKey → Phase 4 的 S1
    //       Phase 3 先做了，Phase 4 后做时应自动拿 Phase 3 的答案
    applyReverseCrossPhaseLoopback() {
      const normalize = (v) => {
        if (v === null || v === undefined) return ''
        const s = String(v).trim()
        return (!s || s === 'undefined' || s === 'null') ? '' : s
      }

      // 当前问卷的所有题目 itemKey 集合
      const currentItemKeys = new Set((this.questionList || []).map(q => normalize(q.itemKey)).filter(Boolean))
      if (currentItemKeys.size === 0) return

      // 遍历跨阶段题目，查找哪些题目的 sourceItemKey 指向当前问卷
      const reverseMatches = [] // { sourceItem, targetItemKey }
      ;(this.crossPhaseGroups || []).forEach(group => {
        ;(group.items || []).forEach(crossItem => {
          try {
            const cfg = JSON.parse(crossItem.configJson || '{}')
            const innerCfg = cfg.__config__ || cfg
            const srcItemKey = normalize(innerCfg.sourceItemKey)
            if (!srcItemKey) return
            // sourceItemKey 指向当前问卷的某个题目 → 反向引用
            if (currentItemKeys.has(srcItemKey)) {
              reverseMatches.push({
                crossItem,       // 其他阶段的题目（有答案的一方）
                targetItemKey: srcItemKey  // 当前问卷被引用的题目
              })
            }
          } catch (e) {}
        })
      })

      if (reverseMatches.length === 0) return

      reverseMatches.forEach(({ crossItem, targetItemKey }) => {
        // 当前题目已有审核答案，不覆盖
        const current = normalize(this.reviewAnswerMap[targetItemKey])
        if (current) return

        // 取跨阶段题目的答案（优先审核答案 → 原始答案）
        const rawAnswer = this.getCrossPhaseAnswerValue(crossItem)
        if (!normalize(rawAnswer)) return

        // 找到当前问卷的目标题目
        const targetQ = this.questionList.find(q => normalize(q.itemKey) === targetItemKey)
        if (!targetQ || !this.isChoiceQuestion(targetQ.itemType)) return

        // 解析答案值
        let values = []
        try {
          const parsed = JSON.parse(rawAnswer)
          values = Array.isArray(parsed) ? parsed.map(String) : [String(parsed)]
        } catch {
          values = [String(rawAnswer)]
        }

        // 仅保留目标题存在的选项值
        const targetOptionValues = new Set((targetQ.options || []).map(o => String(o.value)))
        if (targetOptionValues.size > 0) {
          values = values.filter(v => targetOptionValues.has(String(v)))
        }
        values = [...new Set(values)]
        if (values.length === 0) return

        const finalAnswer = this.isMultipleChoice(targetQ.itemType)
          ? JSON.stringify(values)
          : JSON.stringify([values[0]])

        // 预填审核答案
        this.$set(this.reviewAnswerMap, targetItemKey, finalAnswer)

        // 自动设置审核结果
        const crossAnswer = crossItem.answer || {}
        const crossReviewResult = normalize(crossAnswer.reviewResult1)

        if (targetQ.enableScoring) {
          // 开启评分：根据跨阶段审核结果自动设置 SCORE/NO_SCORE
          if (crossReviewResult === 'SCORE' || crossReviewResult === 'NO_SCORE') {
            this.$set(this.reviewMap, targetItemKey, crossReviewResult)
          } else {
            // 跨阶段未审核，根据答案自动判断得分
            this.updateReviewResultFromAnswer(targetQ, finalAnswer)
          }
        } else {
          // 未开启评分：只回圈选项，自动设为通过
          this.$set(this.reviewMap, targetItemKey, 'SCORE')
        }
      })
    },
    // 解析廉政协议答案 JSON（跨阶段参考资料用）
    parseAgreementAnswer(val) {
      return _parseAgreement(val)
    },
    // 渲染参考资料中的廉政协议 PDF
    async renderCrossPhasePdfs() {
      for (const group of this.crossPhaseGroups) {
        for (const item of group.items) {
          if (item.itemType !== 'INTEGRITY_AGREEMENT') continue
          const val = this.getCrossPhaseAnswerValue(item)
          const { agreementUrl } = _parseAgreement(val)
          if (!agreementUrl) continue
          
          const stateKey = group.phaseNo + '_' + item.itemKey
          const refKey = 'refPdf_' + stateKey
          const container = this.$refs[refKey]?.[0] || this.$refs[refKey]
          if (!container) continue
          
          await renderPdfToContainer(agreementUrl, container, state => {
            this.$set(this.refPdfStates, stateKey, state)
          })
        }
      }
    },
    
    // 打开悬浮媒体播放器
    openFloatingMedia(type, src, title) {
      this.floatingMedia = { visible: true, type, src, title: title || '媒体预览', startTime: 0 }
    },
    // 打开悬浮音频播放器（联动主播放器）
    openFloatingAudio(question) {
      let startTime = 0
      // 直接从 DOM 查找主播放器的 audio 元素
      const audioEl = this.$el.querySelector('.audio-answer audio')
      if (audioEl) {
        startTime = audioEl.currentTime || 0
        audioEl.pause()
      }
      this.floatingMedia = {
        visible: true,
        type: 'audio',
        src: this.getAudioUrl(question.itemKey),
        title: this.stripHtml(question.label),
        startTime
      }
    },
    
    // 关闭悬浮媒体播放器
    closeFloatingMedia() {
      this.floatingMedia.visible = false
    },
    
    // 判断答案值是否包含文件URL
    parseRefFileList(value) {
      if (!value) return []
      const trimmed = value.trim()
      // JSON 数组格式
      if (trimmed.startsWith('[')) {
        try {
          const files = JSON.parse(trimmed)
          if (!Array.isArray(files)) return []
          return files.map(f => {
            if (typeof f === 'string') return { url: f, name: f.split('/').pop() }
            return {
              url: f.url || f.Url || f.fileUrl || f.FileUrl || '',
              name: f.name || f.Name || ''
            }
          }).filter(f => !!f.url)
        } catch { return [] }
      }
      // 纯 URL
      if (trimmed.startsWith('http')) {
        return [{ url: trimmed, name: trimmed.split('/').pop() }]
      }
      return []
    },
    
    // 判断跨阶段题目是选择题
    isCrossPhaseChoiceQuestion(itemType) {
      return ['RADIO', 'CHECKBOX', 'SELECT', 'MULTI_SELECT'].includes(itemType)
    },
    
    // 判断跨阶段题目某选项是否被选中
    isCrossPhaseOptionSelected(item, opt) {
      const val = this.getCrossPhaseAnswerValue(item)
      if (!val) return false
      const str = String(val).trim()
      // JSON 数组格式
      if (str.startsWith('[')) {
        try {
          const arr = JSON.parse(str)
          return Array.isArray(arr) && arr.map(String).includes(String(opt.value))
        } catch { return false }
      }
      return String(val) === String(opt.value)
    },
    
    // 解析廉政协议答案（主审核区用）
    parseAgreementValue(itemKey) {
      return _parseAgreement(this.answerMap[itemKey])
    },
    
    // 渲染审核页廉政协议 PDF
    async renderGradingPdfs() {
      for (const q of this.filteredQuestionList) {
        if (q.itemType !== 'INTEGRITY_AGREEMENT') continue
        const { agreementUrl } = this.parseAgreementValue(q.itemKey)
        if (!agreementUrl) continue
        
        const refKey = 'gradingPdf_' + q.itemKey
        const container = this.$refs[refKey]?.[0] || this.$refs[refKey]
        if (!container) continue
        
        await renderPdfToContainer(agreementUrl, container, state => {
          this.$set(this.gradingPdfStates, q.itemKey, state)
        })
      }
    },
    
    // 检查当前用户是否有权限访问指定审核级别
    canAccessLevel(level) {
      if (this.isAdminUser) return true
      return this.allowedReviewLevels.includes(level)
    },
    handleReviewLevelChange() {
      // 切换审核级别时重新加载当前试卷
      if (this.currentExaminee) {
        this.selectExaminee(this.currentExaminee)
      }
    },
    
    prevExaminee() {
      if (this.currentIndex > 0) {
        this.selectExaminee(this.filteredExamineeList[this.currentIndex - 1])
      }
    },
    
    nextExaminee() {
      if (this.currentIndex < this.filteredExamineeList.length - 1) {
        this.selectExaminee(this.filteredExamineeList[this.currentIndex + 1])
      }
    },
    
    async handleSaveAndBack() {
      this.$emit('back')
    },
    
    // 评审结果变更处理
    // 判断该题审核结果是否锁定（驳回重提交时，上一轮已通过的题目不可修改，仅审核重新提交的题目）
    isReviewLocked(question) {
      if (!this.currentExaminee) return false
      // 考试模式（第三阶段）使用三级审核，不存在驳回机制，不锁定
      if (this.examForm === 1 || this.phase === 3 || this.phase === 0) return false
      // 简化审核模式（阶段1/2/4）：只在驳回重提交场景锁定（auditStatus=0 且有继承的审核结果）
      const auditStatus = this.currentExaminee.auditStatus
      if (auditStatus !== 0) return false
      // 该题有从上一轮继承的审核结果 → 锁定（说明该题之前已通过，无需重新审核）
      return this.preloadedReviewKeys.has(question.itemKey)
    },

    handleReviewChange(question, value) {
      const itemKey = question.itemKey
      const currentResult = this.reviewMap[itemKey]
      
      // 简化审核模式：直接设置，不做得分校验
      if (this.isSimplifiedAudit) {
        this.$set(this.reviewMap, itemKey, value)
        return
      }
      
      // 考试审核模式：得分校验逻辑
      if (currentResult === 'SCORE' && value === 'NOT_APPLICABLE') {
        this.$message.warning('得分选项不能修改为不涉及')
        this.$nextTick(() => {
          this.$set(this.reviewMap, itemKey, 'SCORE')
        })
        return
      }
      
      if (value === 'SCORE' && !this.canSelectScore(question)) {
        this.$message.warning('只有选中正确答案才能选择得分')
        this.$nextTick(() => {
          this.$set(this.reviewMap, itemKey, currentResult || 'NO_SCORE')
        })
        return
      }
      
      this.$set(this.reviewMap, itemKey, value)
    },
    
    // 判断是否可以选择"得分"
    canSelectScore(question) {
      // 检查是否所有选项都有分值设置（perOption模式）
      if (this.hasAllOptionsWithScore(question)) {
        // 需要检查当前选中选项的得分是否达到满分
        const reviewAnswer = this.reviewAnswerMap[question.itemKey]
        if (!reviewAnswer) return false
        
        const fullScore = question.maxScore || 0
        if (fullScore <= 0) return true
        
        try {
          let selectedValues = JSON.parse(reviewAnswer)
          if (!Array.isArray(selectedValues)) selectedValues = [selectedValues]
          
          let positiveSum = 0
          let negativeSum = 0
          selectedValues.forEach(val => {
            const opt = question.options.find(o => String(o.value) === String(val))
            if (opt && opt.score) {
              if (opt.score > 0) positiveSum += opt.score
              else negativeSum += opt.score
            }
          })
          
          let baseScore = 0
          if (positiveSum === 0 && negativeSum < 0) baseScore = fullScore
          let calculated = baseScore + positiveSum
          if (calculated > fullScore) calculated = fullScore
          let earnedScore = calculated + negativeSum
          if (earnedScore < 0) earnedScore = 0
          
          return earnedScore >= fullScore
        } catch {
          return false
        }
      }
      
      // 否则需要判断当前审核答案是否正确
      const reviewAnswer = this.reviewAnswerMap[question.itemKey]
      if (!reviewAnswer) return false
      
      if (!question.correctAnswer) {
        return true  // 没有正确答案的题目，默认可以得分
      }
      
      return this.checkAnswerCorrect(reviewAnswer, question.correctAnswer, question.scoringMethod)
    },

    // 判断是否可以选择"不涉及"
    canSelectNotApplicable(question) {
      const currentResult = this.reviewMap[question.itemKey]
      // 如果当前是得分状态，不允许改为不涉及
      if (currentResult === 'SCORE') {
        return false
      }
      // 不得分状态可以改为不涉及
      return true
    },
    
    // 判断是否使用"每个选项可自定义分值"模式
    hasAllOptionsWithScore(question) {
      // 首先检查 scoringMethod 配置
      if (question.scoringMethod === 'perOption') {
        return true
      }
      
      if (!question.options || question.options.length === 0) {
        return false
      }
      
      const hasAnyScore = question.options.some(opt => opt.score !== undefined && opt.score !== null && opt.score > 0)
      const allScoresDefined = question.options.every(opt => opt.score !== undefined && opt.score !== null)
      
      const result = hasAnyScore && allScoresDefined
      return result
    },
    
    // 判断某一级别是否可编辑
    canEditLevel(level) {
      if (!this.currentExaminee) return true  // 没选择试卷时都可点击
      const status = this.currentExaminee.auditStatus || 0
      // 允许编辑当前状态及之后的级别（例如：一审完成时，可以修改一审；只有进入二审后才不能修改一审）
      return level >= status
    },
    
    // 提交当前级别的审核
    async handleSubmitReview() {
      if (!this.currentExaminee) {
        this.$message.warning('请先选择试卷')
        return
      }
      
      if (!this.canEditCurrentLevel) {
        this.$message.warning('当前级别已完成审核，不可修改')
        return
      }
      
      // 检查是否所有题目都已评审（排除未开启评分的题目，简化审核模式下全部检查）
      // 签字题、分割线、图片展示等非内容题型不需要审核
      // 驳回重审场景：锁定的题目已有上一轮的结果，不需要重新审核
      const NON_REVIEW_TYPES = ['SIGNATURE', 'DIVIDER', 'IMG_DISPLAY', 'IMAGE_DISPLAY', 'CAROUSEL', 'IMAGE_CAROUSEL']
      const shouldReview = q => !NON_REVIEW_TYPES.includes(q.itemType) && !this.isRatingQuestion(q.itemType) && (q.enableScoring || this.isSimplifiedAudit) && !this.isReviewLocked(q)
      const qTitle = q => q.serialNo ? q.serialNo + '.' + this.stripHtml(q.label || '') : '第' + (this.filteredQuestionList.indexOf(q) + 1) + '题'
      const unreviewed = this.filteredQuestionList.filter(q => shouldReview(q) && !this.reviewMap[q.itemKey])
      if (unreviewed.length > 0) {
        this.$message.warning(`还有 ${unreviewed.length} 道题目未评审`)
        return
      }
      
      // 检查不得分题目的失分备注必填
      if (!this.isSimplifiedAudit) {
        for (const q of this.filteredQuestionList) {
          if (!shouldReview(q)) continue
          if (this.reviewMap[q.itemKey] === 'NO_SCORE') {
            const remark = this.remarkMap[q.itemKey]
            if (!remark || !remark.trim()) {
              const idx = this.filteredQuestionList.indexOf(q)
              this.$message.warning(`${qTitle(q)} 为不得分，请填写失分备注`)
              this.scrollToQuestion(idx)
              return
            }
          }
        }
      }
      
      // 检查选择题是否已勾选答案（仅考试审核模式，简化审核不需要重新选择）
      if (!this.isSimplifiedAudit) {
        for (const q of this.filteredQuestionList) {
          if (!shouldReview(q)) continue
          if (!this.isChoiceQuestion(q.itemType)) continue
          const answer = this.reviewAnswerMap[q.itemKey]
          if (!answer || (Array.isArray(answer) && answer.length === 0)) {
            const idx = this.filteredQuestionList.indexOf(q)
            this.$message.warning(`${qTitle(q)} 未勾选选项，请选择答案`)
            this.scrollToQuestion(idx)
            return
          }
        }
      }
      
      // 检查明检文本题是否已填写审核答案（仅考试审核模式）
      if (!this.isSimplifiedAudit) {
        for (const q of this.filteredQuestionList) {
          if (!shouldReview(q)) continue
          if (q.itemType !== 'MINGJIAN_PHOTO_TEXT') continue
          const answer = this.reviewAnswerMap[q.itemKey]
          if (!answer || !String(answer).trim()) {
            const idx = this.filteredQuestionList.indexOf(q)
            this.$message.warning(`${qTitle(q)} 未填写审核答案`)
            this.scrollToQuestion(idx)
            return
          }
        }
      }
      
      this.submitLoading = true
      try {
        // 构建审核结果（按当前可审核题目全集构建，避免单题答案漏传）
        const NON_REVIEW_TYPES = ['SIGNATURE', 'DIVIDER', 'IMG_DISPLAY', 'IMAGE_DISPLAY', 'CAROUSEL', 'IMAGE_CAROUSEL']
        const shouldReview = q => !NON_REVIEW_TYPES.includes(q.itemType) && !this.isRatingQuestion(q.itemType) && (q.enableScoring || this.isSimplifiedAudit) && !this.isReviewLocked(q)

        const results = (this.filteredQuestionList || [])
          .filter(q => shouldReview(q))
          .map(q => {
            const itemKey = q.itemKey
            const relationIds = Array.isArray(q.relationIds) ? q.relationIds.filter(Boolean) : []

            const rawAnswer = this.reviewAnswerMap[itemKey]
            let normalizedAnswer = ''
            if (rawAnswer !== null && rawAnswer !== undefined) {
              if (typeof rawAnswer === 'string') {
                normalizedAnswer = rawAnswer
              } else if (Array.isArray(rawAnswer) || typeof rawAnswer === 'object') {
                try {
                  normalizedAnswer = JSON.stringify(rawAnswer)
                } catch {
                  normalizedAnswer = String(rawAnswer)
                }
              } else {
                normalizedAnswer = String(rawAnswer)
              }
            }


            return {
              ItemKey: itemKey,
              Result: this.reviewMap[itemKey] || '',
              Answer: normalizedAnswer,  // 发送修改后的答案（统一字符串化）
              Remark: this.remarkMap[itemKey] || '',
              SpecialRemark: this.reviewSpecialRemarkMap[itemKey] || '',
              RelationIds: relationIds
            }
          })
        
        const { submitReview } = await import('index/api/project/form')
        const reviewRes = await submitReview({
          AnswerKey: this.currentExaminee.answerKey,
          // 简化审核（阶段1/2/4）统一按一审提交，避免写到review_answer_2/3导致刷新不回显
          ReviewLevel: this.isSimplifiedAudit ? 1 : this.reviewLevel,
          Reviewer: 'Admin', // TODO: 从用户信息获取
          Phase: this.phase || 0,
          Results: results
        })
        
        // 更新当前试卷的审核状态（使用后端返回的实际状态）
        const actualStatus = reviewRes?.data?.data?.auditStatus ?? reviewRes?.data?.auditStatus ?? this.reviewLevel
        this.currentExaminee.auditStatus = actualStatus
        // 同时更新列表中的状态（确保列表显示正确）
        const idx = this.examineeList.findIndex(e => e.answerKey === this.currentExaminee.answerKey)
        if (idx >= 0) {
          this.$set(this.examineeList[idx], 'auditStatus', actualStatus)
        }
        
        // 清除该考生的缓存，确保下次加载时获取最新的审核详情
        if (this.answerCache[this.currentExaminee.answerKey]) {
          delete this.answerCache[this.currentExaminee.answerKey].reviewDetail
        }
        
        // 提交成功后清除草稿
        this.clearDraft()
        this.$message.success(this.phase > 0 ? '审核提交成功' : `${this.reviewLevelText}审提交成功`)
        
        if (!this.isSimplifiedAudit && this.reviewLevel < 3) {
          // 考试模式（Phase=0 或 Phase=3）：自动切换到下一审核级别
          const nextLevel = this.reviewLevel + 1
          if (this.canAccessLevel(nextLevel)) {
            this.reviewLevel = nextLevel
            this.handleReviewLevelChange()
          } else {
            // 无下一级权限，跳到下一份
            if (this.currentIndex < this.filteredExamineeList.length - 1) {
              this.nextExaminee()
            }
          }
        } else {
          // 简化审核模式 或 已到三审：跳到下一份
          if (this.currentIndex < this.filteredExamineeList.length - 1) {
            this.nextExaminee()
          }
        }
      } catch (e) {
        console.error('[ExamGrading] Submit review failed:', e)
        const msg = e?.data?.message || e?.response?.data?.message || e?.message || '提交失败'
        this.$message.error(msg)
      } finally {
        this.submitLoading = false
      }
    },
    
    // ===== 草稿功能 =====
    getDraftKey(examinee) {
      if (!examinee) return null
      return `review_draft_${this.formKey}_${examinee.answerKey}_${this.reviewLevel}`
    },
    
    hasDraft(examinee) {
      const key = `review_draft_${this.formKey}_${examinee.answerKey}`
      // 检查任意审核级别是否有草稿
      for (let l = 1; l <= 3; l++) {
        if (localStorage.getItem(key + '_' + l)) return true
      }
      return false
    },
    
    saveDraft() {
      if (!this.currentExaminee) return
      const key = this.getDraftKey(this.currentExaminee)
      if (!key) return
      const draft = {
        reviewMap: { ...this.reviewMap },
        remarkMap: { ...this.remarkMap },
        reviewAnswerMap: { ...this.reviewAnswerMap },
        reviewSpecialRemarkMap: { ...this.reviewSpecialRemarkMap },
        savedAt: new Date().toISOString()
      }
      try {
        localStorage.setItem(key, JSON.stringify(draft))
        this.$message.success('草稿已保存')
      } catch (e) {
        console.error('[ExamGrading] Save draft failed:', e)
        this.$message.error('保存草稿失败')
      }
    },
    
    restoreDraft() {
      if (!this.currentExaminee) return
      const key = this.getDraftKey(this.currentExaminee)
      if (!key) return
      const raw = localStorage.getItem(key)
      if (!raw) return
      try {
        const draft = JSON.parse(raw)
        // 恢复审核结果
        if (draft.reviewMap) {
          Object.entries(draft.reviewMap).forEach(([k, v]) => {
            this.$set(this.reviewMap, k, v)
          })
        }
        if (draft.remarkMap) {
          Object.entries(draft.remarkMap).forEach(([k, v]) => {
            this.$set(this.remarkMap, k, v)
          })
        }
        if (draft.reviewAnswerMap) {
          Object.entries(draft.reviewAnswerMap).forEach(([k, v]) => {
            this.$set(this.reviewAnswerMap, k, v)
          })
        }
        if (draft.reviewSpecialRemarkMap) {
          Object.entries(draft.reviewSpecialRemarkMap).forEach(([k, v]) => {
            this.$set(this.reviewSpecialRemarkMap, k, v)
          })
        }
        const savedAt = draft.savedAt ? new Date(draft.savedAt).toLocaleString() : ''
        this.$message.info(`已恢复草稿${savedAt ? '（' + savedAt + '）' : ''}`)
      } catch (e) {
        console.warn('[ExamGrading] Restore draft failed:', e)
      }
    },
    
    clearDraft() {
      if (!this.currentExaminee) return
      const key = this.getDraftKey(this.currentExaminee)
      if (key) localStorage.removeItem(key)
    },
    
    getStatusClass(examinee) {
      const status = examinee.auditStatus || 0
      if (status === -1) return 'status-rejected'
      // 简化审核模式（阶1/2/4）：已通过直接绿色
      if (this.isSimplifiedAudit) {
        return status >= 1 ? 'status-complete' : 'status-pending'
      }
      if (status >= 3) return 'status-complete'
      if (status >= 2) return 'status-review2'
      if (status >= 1) return 'status-review1'
      return 'status-pending'
    },
    
    getStatusText(examinee) {
      const status = examinee.auditStatus || 0
      if (status === -1) return '已驳回'
      // 简化审核模式（阶1/2/4）：只有通过/不通过
      if (this.isSimplifiedAudit) {
        return status >= 1 ? '已通过' : '待审核'
      }
      if (status >= 3) return '三审完成'
      if (status >= 2) return '二审完成'
      if (status >= 1) return '一审完成'
      return '待审核'
    },
    
    isPaginationItem(type) {
      return ['PAGINATION', 'PAGE_BREAK', 'DIVIDER', 'DESCRIPTION'].includes(type)
    },
    
    getTypeName(type) {
      const map = {
        // 单选题
        'RADIO': '单选题',
        'SINGLE_CHOICE': '单选题',
        // 多选题
        'CHECKBOX': '多选题',
        'MULTIPLE_CHOICE': '多选题',
        // 文本输入类
        'TEXT': '文本',
        'INPUT': '单行文本',
        'TEXTAREA': '多行文本',
        'NUMBER': '数字',
        'EMAIL': '邮箱',
        'PHONE': '电话',
        'MOBILE_INPUT': '手机号',
        'ID_CARD': '身份证号',
        'FILL_BLANK': '填空题',
        // 评分类
        'RATING': '评分',
        'RATE': '评分',
        'SLIDER': '滑块',
        'NPS': 'NPS评分',
        // 日期时间类
        'DATE': '日期',
        'DATETIME': '日期时间',
        'DATE_PICKER': '日期选择',
        'DATE_RANGE': '日期范围',
        'DATERANGE': '日期范围',
        'TIME': '时间',
        'TIME_PICKER': '时间选择',
        // 下拉选择类
        'SELECT': '下拉选择',
        'MULTI_SELECT': '多选下拉',
        'CASCADER': '级联选择',
        'CASCADER_MULTI': '级联多选',
        // 图片选择类
        'IMG_RADIO': '图片单选',
        'IMG_CHECKBOX': '图片多选',
        'IMAGE_SELECT': '图片选择',
        'IMAGE_CHOICE': '图片选择',
        // 排序题
        'SORT': '排序题',
        // 文件上传类
        'FILE': '文件上传',
        'IMAGE': '图片上传',
        'FILE_UPLOAD': '文件上传',
        'IMG_UPLOAD': '图片上传',
        // 矩阵题
        'MATRIX_RADIO': '矩阵单选',
        'MATRIX_CHECKBOX': '矩阵多选',
        'MATRIX_SCALE': '矩阵量表',
        'MATRIX_DROPDOWN': '矩阵下拉',
        'MATRIX_SELECT': '矩阵选择',
        // 横向填空
        'HORIZONTAL_INPUT': '横向填空',
        // 检查项
        'CHECKLIST': '检查项',
        // 签名定位类
        'SIGNATURE': '签名',
        'ADDRESS': '地址',
        'LOCATION': '定位',
        'REGION': '省市区',
        // 录音录像类
        'AUDIO': '录音',
        'AUDIO_RECORD': '后台录音',
        'AUDIO_RECORDING': '后台录音',
        'VIDEO': '录像',
        'VIDEO_RECORD': '视频录制',
        // 预约类
        'RESERVE_DATE': '预约日期',
        'RESERVE_TIME': '预约时段',
        // 展示类（辅助题型）
        'DIVIDER': '分割线',
        'IMAGE_DISPLAY': '图片展示',
        'IMG_DISPLAY': '图片展示',
        'CAROUSEL': '图片轮播',
        'IMAGE_CAROUSEL': '图片轮播',
        'EXTERNAL_LINK': '外部链接',
        'INTEGRITY_AGREEMENT': '廉政协议',
        'PAGINATION': '分页',
        // 明检分类题型
        'MINGJIAN_PHOTO_RADIO': '明检-拍照单选',
        'MINGJIAN_PHOTO_CHECKBOX': '明检-拍照多选',
        'MINGJIAN_PHOTO_TEXT': '明检-拍照文本'
      }
      return map[type] || type
    },
    
    isChoiceQuestion(type) {
      return ['RADIO', 'CHECKBOX', 'SINGLE_CHOICE', 'MULTIPLE_CHOICE', 'SELECT', 'MINGJIAN_PHOTO_RADIO', 'MINGJIAN_PHOTO_CHECKBOX'].includes(type)
    },
    
    // 是否是多选题
    isMultipleChoice(type) {
      return ['CHECKBOX', 'MULTIPLE_CHOICE', 'MINGJIAN_PHOTO_CHECKBOX'].includes(type)
    },
    
    // 是否是明检分类题
    isMingjianPhotoQuestion(type) {
      return ['MINGJIAN_PHOTO_RADIO', 'MINGJIAN_PHOTO_CHECKBOX', 'MINGJIAN_PHOTO_TEXT'].includes(type)
    },
    // 获取明检分类题的拍照图片
    getMingjianPhotos(itemKey) {
      const val = this.answerMap[itemKey]
      if (!val) return []
      try {
        const parsed = typeof val === 'string' ? JSON.parse(val) : val
        return parsed.photos || []
      } catch { return [] }
    },
    // 获取明检分类-文本题的文字内容
    getMingjianTextValue(itemKey) {
      const val = this.answerMap[itemKey]
      if (!val) return ''
      try {
        const parsed = typeof val === 'string' ? JSON.parse(val) : val
        return parsed.textValue || ''
      } catch { return String(val) }
    },
    // 获取明检分类答案中的"其他"补充文本
    getMingjianOtherText(itemKey) {
      const val = this.answerMap[itemKey]
      if (!val) return ''
      try {
        const parsed = typeof val === 'string' ? JSON.parse(val) : val
        return parsed.otherText || ''
      } catch { return '' }
    },
    // 判断明检分类原始答案中某选项是否被选中
    isMingjianOriginalSelected(question, opt) {
      const val = this.answerMap[question.itemKey]
      if (!val) return false
      try {
        const parsed = typeof val === 'string' ? JSON.parse(val) : val
        if (parsed.selectedValue !== undefined) {
          return String(parsed.selectedValue) === String(opt.value)
        }
        if (Array.isArray(parsed.selectedValues)) {
          return parsed.selectedValues.map(String).includes(String(opt.value))
        }
        return false
      } catch { return false }
    },
    
    // 是否是文件上传题
    isFileUploadQuestion(type) {
      return ['FILE_UPLOAD', 'IMAGE_UPLOAD', 'UPLOAD', 'FILE', 'IMAGE', 'IMG_UPLOAD'].includes(type)
    },
    
    // 是否是录音题
    isAudioRecordQuestion(type) {
      return ['AUDIO_RECORD', 'AUDIO_RECORDING', 'VOICE_RECORD', 'RECORDING'].includes(type)
    },
    
    // 获取录音URL（确保是完整的绝对路径）
    getAudioUrl(itemKey) {
      const answer = this.answerMap[itemKey]
      if (!answer) return ''
      
      let url = answer
      // 如果是JSON对象，取url属性
      if (typeof answer === 'object' && answer.url) {
        url = answer.url
      } else if (typeof answer === 'string') {
        try {
          const parsed = JSON.parse(answer)
          url = parsed.url || answer
        } catch {
          url = answer
        }
      }
      
      // 确保URL是完整的
      if (url && !url.startsWith('http')) {
        if (url.startsWith('//')) {
          url = 'https:' + url
        } else if (url.includes('.aliyuncs.com')) {
          url = 'https://' + url
        }
      }
      
      return url
    },
    
    // 获取录音文件名（用于下载）
    getAudioFilename(question, examinee) {
      const dealerCode = examinee?.dealerCode || '未知店铺'
      const questionIndex = this.filteredQuestionList.findIndex(q => q.itemKey === question.itemKey) + 1
      return `${dealerCode}_第${questionIndex}题录音`
    },
    
    // 判断是否是图片文件
    isImage(url) {
      if (!url) return false
      // 移除查询参数后检查扩展名
      const urlPath = url.split('?')[0]
      return /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(urlPath) || url.includes('/image/')
    },
    
    // 判断是否是视频文件
    isVideo(url) {
      if (!url) return false
      const urlPath = url.split('?')[0]
      return /\.(mp4|webm|mov|avi|mkv|flv|wmv)$/i.test(urlPath) || url.includes('/video/')
    },
    
    // 判断是否是音频文件
    isAudio(url) {
      if (!url) return false
      const urlPath = url.split('?')[0]
      // .webm 录音文件（路径含 recordings）也算音频
      if (/\.webm$/i.test(urlPath) && url.includes('recordings')) return true
      return /\.(mp3|wav|ogg|aac|m4a|flac|wma|webm)$/i.test(urlPath) || url.includes('/audio/')
    },

    // 判断是否是 PDF 文件
    isPdf(url) {
      if (!url) return false
      const urlPath = url.split('?')[0].toLowerCase()
      return urlPath.endsWith('.pdf') || url.includes('/pdf/')
    },
    
    // 获取文件列表（从答案中解析）
    getFileList(itemKey) {
      const answer = this.answerMap[itemKey]
      if (!answer) return []
      
      
      // 确保URL是完整的绝对路径
      const ensureFullUrl = (url) => {
        if (!url) return ''
        const trimmedUrl = String(url).trim()
        
        // 已经是完整URL
        if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
          return trimmedUrl
        }
        // 协议相对URL
        if (trimmedUrl.startsWith('//')) {
          return 'https:' + trimmedUrl
        }
        // OSS域名但缺少协议（如 bucket.oss-cn-xxx.aliyuncs.com/...）
        if (trimmedUrl.includes('.aliyuncs.com') || trimmedUrl.includes('.oss-cn-')) {
          return 'https://' + trimmedUrl
        }
        // 相对路径需要补全
        if (trimmedUrl.startsWith('/')) {
          return window.location.origin + trimmedUrl
        }
        // 尝试作为完整URL返回（可能是其他CDN格式）
        return trimmedUrl
      }
      
      // 处理对象类型的答案（可能是 { url, name } 或数组）
      if (typeof answer === 'object' && answer !== null) {
        if (Array.isArray(answer)) {
          return answer.map(item => {
            if (typeof item === 'object' && item.url) {
              const finalUrl = ensureFullUrl(item.url)
              return { url: finalUrl, name: item.name || '' }
            }
            const finalUrl = ensureFullUrl(String(item))
            return { url: finalUrl, name: '' }
          })
        }
        if (answer.url) {
          const finalUrl = ensureFullUrl(answer.url)
          return [{ url: finalUrl, name: answer.name || '' }]
        }
        // 对象但无 url 属性，尝试遍历值
        return []
      }
      
      // 字符串类型，尝试解析 JSON
      try {
        const parsed = JSON.parse(answer)
        if (Array.isArray(parsed)) {
          return parsed.map(item => {
            if (typeof item === 'object' && item.url) {
              const finalUrl = ensureFullUrl(item.url)
              return { url: finalUrl, name: item.name || '' }
            }
            return { url: ensureFullUrl(String(item)), name: '' }
          })
        }
        if (typeof parsed === 'object' && parsed.url) {
          return [{ url: ensureFullUrl(parsed.url), name: parsed.name || '' }]
        }
        return [{ url: ensureFullUrl(String(parsed)), name: '' }]
      } catch {
        // 非 JSON 格式，可能是逗号分隔的 URL 或纯URL
        if (answer.includes(',')) {
          return answer.split(',').filter(u => u.trim()).map(url => ({ url: ensureFullUrl(url.trim()), name: '' }))
        }
        return [{ url: ensureFullUrl(answer), name: '' }]
      }
    },
    
    // 判断是否是"其他"选项
    isOtherOption(opt) {
      if (!opt) return false
      // 检查 isOther 标志
      if (opt.isOther === true) return true
      // 检查 value 是否以 'other_' 开头（后端生成的"其他"选项通常有这样的值）
      if (opt.value && String(opt.value).toLowerCase().startsWith('other_')) return true
      // 检查标签文本
      const labelText = (opt.label || '').replace(/<[^>]*>/g, '').trim()
      return labelText === '其他' || labelText.toLowerCase() === 'other'
    },
    
    // 获取"其他"选项的文本内容
    getOtherText(itemKey, opt) {
      // 优先从直接存储的 otherTextMap 中获取
      if (this.otherTextMap && this.otherTextMap[itemKey]) {
        return this.otherTextMap[itemKey]
      }
      return ''
    },
    
    isCorrectOption(question, option) {
      if (!question.correctAnswer) return false
      try {
        const correct = JSON.parse(question.correctAnswer)
        if (Array.isArray(correct)) {
          return correct.includes(option.value)
        }
        return correct === option.value
      } catch {
        return String(question.correctAnswer) === String(option.value)
      }
    },
    
    // 是否是用户原始选中的选项
    isOriginalSelected(question, option) {
      const answer = this.answerMap[question.itemKey]
      if (!answer) return false
      const optValue = String(option.value)
      const optLabel = this.stripHtml(option.label || '')
      try {
        const selected = JSON.parse(answer)
        if (Array.isArray(selected)) {
          return selected.some(v => String(v) === optValue || String(v) === optLabel)
        }
        return String(selected) === optValue || String(selected) === optLabel
      } catch {
        // 非 JSON，直接字符串比较
        const strAnswer = String(answer)
        return strAnswer === optValue || strAnswer === optLabel ||
               this.stripHtml(strAnswer) === optValue || this.stripHtml(strAnswer) === optLabel
      }
    },

    // 是否是一审选中的选项（二审/三审时显示，仅一审得分时才标记）
    isReview1Selected(question, option) {
      if (this.reviewLevel < 2) return false
      // 一审不得分，不显示"一审"标签
      if (this.reviewResult1Map[question.itemKey] !== 'SCORE') return false
      const answer = this.review1AnswerMap[question.itemKey]
      if (!answer) return false
      return this.isAnswerMatchOption(answer, option)
    },

    // 是否是二审选中的选项（三审时显示，仅二审得分时才标记）
    isReview2Selected(question, option) {
      if (this.reviewLevel < 3) return false
      // 二审不得分，不显示"二审"标签
      if (this.reviewResult2Map[question.itemKey] !== 'SCORE') return false
      const answer = this.review2AnswerMap[question.itemKey]
      if (!answer) return false
      return this.isAnswerMatchOption(answer, option)
    },
    
    // 辅助方法：判断答案是否匹配选项
    isAnswerMatchOption(answer, option) {
      try {
        const selected = JSON.parse(answer)
        const optValue = String(option.value)
        if (Array.isArray(selected)) {
          return selected.some(v => String(v) === optValue)
        }
        return String(selected) === optValue
      } catch {
        return String(answer) === String(option.value) || 
               this.stripHtml(answer) === this.stripHtml(option.value)
      }
    },

    // 按题判断是否应显示原答案标记（蓝色勾+"原答案"标签）
    // 多级审核编辑模式下，一审不得分的题不显示原答案，避免视觉上误以为被回圈
    canShowOriginalForQuestion(question) {
      if (!this.shouldShowOriginalMarks) return false
      // 用户手动操作过该题目后，不再显示蓝色原答案标记，避免"取消不掉"的混乱
      if (this.touchedQuestions[question.itemKey]) return false
      if (this.isSimplifiedAudit || this.isReadOnlyMode) return true
      // 多级审核编辑模式：二审只对一审得分的题显示原答案；三审只对二审得分的题显示
      if (this.reviewLevel === 2) return this.reviewResult1Map[question.itemKey] === 'SCORE'
      if (this.reviewLevel === 3) return this.reviewResult2Map[question.itemKey] === 'SCORE'
      return true
    },

    // 是否是审核人选中的选项
    isReviewSelected(question, option) {
      const reviewAnswer = this.reviewAnswerMap[question.itemKey]
      if (!reviewAnswer) return false
      try {
        const selected = JSON.parse(reviewAnswer)
        const optValue = String(option.value)
        if (Array.isArray(selected)) {
          return selected.some(v => String(v) === optValue)
        }
        return String(selected) === optValue
      } catch {
        return String(reviewAnswer) === String(option.value)
      }
    },
    
    // 格式化题目数据为 Question 组件期望的格式
    formatQuestionItem(question) {
      return {
        item_key: question.itemKey,
        item_type: question.itemType,
        label: question.label,
        description: question.description || '',
        tip_content: question.tipContent || '',
        config_json: question.configJson || '{}',
        options: question.options
      }
    },
    
    // 获取审核答案（优先显示审核修改后的答案）
    getReviewAnswer(itemKey) {
      return this.reviewAnswerMap[itemKey] || this.answerMap[itemKey] || null
    },
    
    // 获取选项标签文本
    getOptionLabel(opt) {
      if (!opt) return ''
      return this.stripHtml(opt.label || opt.text || String(opt.value))
    },
    
    // 处理 Question 组件的答案变更
    handleQuestionChange(payload) {
      const { itemKey, value } = payload
      this.$set(this.reviewAnswerMap, itemKey, value)
      // 找到对应的 question 对象并更新评审结果
      const question = this.questionList.find(q => q.itemKey === itemKey)
      if (question) {
        this.updateReviewResultFromAnswer(question, value)
      }
    },

    // 是否允许编辑审核答案：以后端权威关系字段 + 元数据兜底
    // 规则：仅“被引用题(target)”可编辑；来源题(source)与普通题不可编辑
    canEditReviewAnswer(question) {
      if (!this.isChoiceQuestion(question?.itemType) && question?.itemType !== 'MINGJIAN_PHOTO_TEXT') return false

      // 简化审核模式（阶段1/2/4）：所有选择题都可编辑审核答案
      if (this.isSimplifiedAudit && !this.isReadOnlyMode) return true

      // 阶段3（多级审核）：所有选择题都可编辑审核答案，不受回圈限制
      if (!this.isSimplifiedAudit && this.examForm === 1 && !this.isReadOnlyMode) return true

      const normalize = (v) => {
        if (v === null || v === undefined) return ''
        const s = String(v).trim()
        return (!s || s === 'undefined' || s === 'null') ? '' : s
      }

      // 后端显式标记
      const isSource = question?.isLoopbackSource === true
      const isTargetByFlag = question?.isLoopbackReference === true || question?.isReferenced === true

      // 元数据兜底：存在 sourceFormKey/sourceItemKey 或 relationIds 或 optionMapping
      const hasTargetBinding = !!(normalize(question?.sourceFormKey) && normalize(question?.sourceItemKey))
      const hasRelationIds = Array.isArray(question?.relationIds) && question.relationIds.some(x => normalize(x))
      const hasOptionMapping = !!question?.optionMapping

      // 历史数据兜底：数据源类型为 questionnaire 的选择题，视为回圈题
      const dsType = normalize(question?.dataSourceType).toLowerCase()
      const isQuestionnaireSource = dsType === 'questionnaire'

      // target 优先：只要命中任一回圈特征，就允许编辑，避免被 isLoopbackSource 脏标记误伤
      const isTarget = isTargetByFlag || hasTargetBinding || hasRelationIds || hasOptionMapping || isQuestionnaireSource
      if (isTarget) return true

      // 非回圈题保持不可编辑
      return !isSource && false
    },

    // 点击切换审核选项
    toggleReviewOption(question, option) {
      const isMultiple = this.isMultipleChoice(question.itemType)
      let current = this.reviewAnswerMap[question.itemKey] || ''
      let selected = []

      // 标记用户已手动操作该题目，停止显示蓝色原答案标记
      this.$set(this.touchedQuestions, question.itemKey, true)

      // 解析当前选中状态 - 确保正确解析字符串类型
      if (current) {
        try {
          const parsed = JSON.parse(current)
          if (Array.isArray(parsed)) {
            selected = parsed.map(String)
          } else {
            selected = [String(parsed)]
          }
        } catch {
          selected = current.split(',').map(s => s.trim()).filter(Boolean)
        }
      }

      const optValue = String(option.value)
      // 使用findIndex并确保类型一致比较
      const idx = selected.findIndex(v => String(v) === optValue)

      if (isMultiple) {
        // 多选：切换选中状态
        if (idx >= 0) {
          selected.splice(idx, 1)
        } else {
          selected.push(optValue)
        }

        // 应用互斥逻辑
        if (question.exclusive) {
          // 优先使用分组配置
          const exclusiveGroups = question.exclusiveGroups || []
          if (exclusiveGroups.length > 0) {
            // 找到新选中选项所属的分组
            let clickedGroupId = null
            exclusiveGroups.forEach(group => {
              if (group.options && group.options.map(String).includes(optValue)) {
                clickedGroupId = group.id
              }
            })

            if (clickedGroupId && idx < 0) {
              // 刚刚选中了属于某分组的选项 -> 移除其他分组的选项
              selected = selected.filter(val => {
                let valGroupId = null
                exclusiveGroups.forEach(group => {
                  if (group.options && group.options.map(String).includes(val)) {
                    valGroupId = group.id
                  }
                })
                // 保留：相同分组的选项 或 不属于任何分组的选项
                return valGroupId === null || valGroupId === clickedGroupId
              })
            }
          } else if (question.exclusiveOptions && question.exclusiveOptions.length > 0) {
            // 兼容旧版：使用 exclusiveOptions 配置
            const exclusiveOptStrs = question.exclusiveOptions.map(v => String(v))
            const clickedIsExclusive = exclusiveOptStrs.includes(optValue)
            const wasExclusiveSelected = selected.filter(v => v !== optValue).some(v => exclusiveOptStrs.includes(v))

            if (clickedIsExclusive && idx < 0) {
              // 刚刚选中了互斥选项 -> 只保留该互斥选项，取消其他所有
              selected = [optValue]
            } else if (!clickedIsExclusive && wasExclusiveSelected) {
              // 在互斥选项已选中的情况下选了其他 -> 取消所有互斥选项
              selected = selected.filter(v => !exclusiveOptStrs.includes(v))
            }
          }
        }
      } else {
        // 单选：如果已选中则取消，否则选中新选项
        if (idx >= 0) {
          selected = []
        } else {
          selected = [optValue]
        }
      }

      // 保存新的审核答案
      const newAnswer = selected.length > 0 ? JSON.stringify(selected) : ''

      // 使用 $set 确保响应式更新
      this.$set(this.reviewAnswerMap, question.itemKey, newAnswer)

      // 自动同步到”引用了当前题目”的题（仅单选/多选）
      this.syncLoopbackReferencedAnswers(question, newAnswer)

      // 阶段1/2/4：回圈题填写后，反向回填到它引用的目标题目（覆盖答案）
      if (this.isSimplifiedAudit) {
        this.syncLoopbackReverseToSource(question, newAnswer)
      }

      // 按”当前审核答案 + 原答案”实时重新计算题目触发显示
      this.applyLogicVisibility()

      // 递增计数器强制计算属性重新计算
      this.updateCounter++

      // 自动更新评审结果
      this.updateReviewResultFromAnswer(question, newAnswer)
    },

    // 回圈同步：当前题答案变化后，更新引用它的题目答案
    syncLoopbackReferencedAnswers(sourceQuestion, sourceAnswer) {
      if (!this.isChoiceQuestion(sourceQuestion?.itemType)) return
      const sourceKey = sourceQuestion?.itemKey
      if (!sourceKey) return

      const normalize = (v) => {
        if (v === null || v === undefined) return ''
        const s = String(v).trim()
        return (s === 'undefined' || s === 'null') ? '' : s
      }

      const sourceKeys = this.getLoopbackCandidateKeys(sourceQuestion).map(normalize)
      const targets = (this.questionList || []).filter(q => {
        if (!q || q.itemKey === sourceKey) return false
        const qSourceItemKey = normalize(q.sourceItemKey)
        return qSourceItemKey && sourceKeys.includes(qSourceItemKey)
      })

      targets.forEach(target => {
        if (!this.isChoiceQuestion(target.itemType)) return

        const normalizedAnswer = this.normalizeLoopbackAnswerForTarget(sourceAnswer, target)
        this.$set(this.reviewAnswerMap, target.itemKey, normalizedAnswer)
        this.updateReviewResultFromAnswer(target, normalizedAnswer)
      })
    },

    // 阶段1/2/4：回圈题编辑后，反向回填到它引用的源题目（覆盖答案）
    syncLoopbackReverseToSource(editedQuestion, newAnswer) {
      if (!this.isChoiceQuestion(editedQuestion?.itemType)) return

      const normalize = (v) => {
        if (v === null || v === undefined) return ''
        const s = String(v).trim()
        return (s === 'undefined' || s === 'null') ? '' : s
      }

      // 获取回圈题引用的源题 itemKey
      const sourceItemKey = normalize(editedQuestion?.sourceItemKey)
      if (!sourceItemKey) return

      // 在题目列表中找到源题
      const sourceQuestion = (this.questionList || []).find(q => {
        if (!q) return false
        const candidateKeys = this.getLoopbackCandidateKeys(q).map(normalize)
        return candidateKeys.includes(sourceItemKey)
      })

      if (!sourceQuestion || !this.isChoiceQuestion(sourceQuestion.itemType)) return

      // 反向映射：将编辑题的答案转换为源题的格式
      const reverseAnswer = this.normalizeLoopbackAnswerReverse(newAnswer, editedQuestion, sourceQuestion)
      this.$set(this.reviewAnswerMap, sourceQuestion.itemKey, reverseAnswer)
      this.updateReviewResultFromAnswer(sourceQuestion, reverseAnswer)
    },

    // 反向映射：将回圈题答案转换为源题可用格式（反转 optionMapping）
    normalizeLoopbackAnswerReverse(answer, editedQuestion, sourceQuestion) {
      if (!answer) return ''

      let values = []
      try {
        const parsed = JSON.parse(answer)
        values = Array.isArray(parsed) ? parsed.map(String) : [String(parsed)]
      } catch {
        values = [String(answer)]
      }

      if (values.length === 0) return ''

      // 反转 optionMapping：{ sourceVal: targetVal } → { targetVal: sourceVal }
      const mapping = this.normalizeOptionMapping(editedQuestion && editedQuestion.optionMapping)
      if (mapping) {
        const reverseMap = {}
        Object.keys(mapping).forEach(k => {
          reverseMap[String(mapping[k])] = String(k)
        })
        values = values.map(v => {
          const key = String(v)
          return Object.prototype.hasOwnProperty.call(reverseMap, key) ? reverseMap[key] : key
        })
      }

      // 只保留在源题 options 中存在的值
      const sourceOptionValues = new Set(((sourceQuestion && sourceQuestion.options) || []).map(o => String(o.value)))
      if (sourceOptionValues.size > 0) {
        values = values.filter(v => sourceOptionValues.has(String(v)))
      }

      values = [...new Set(values)]
      if (values.length === 0) return ''

      // 根据源题题型输出格式
      if (this.isMultipleChoice(sourceQuestion.itemType)) {
        return JSON.stringify(values)
      }
      return JSON.stringify([values[0]])
    },

    // 将源题答案规范为目标题可用格式（支持 optionMapping 覆盖）
    normalizeLoopbackAnswerForTarget(answer, targetQuestion) {
      if (!answer) return ''

      let values = []
      try {
        const parsed = JSON.parse(answer)
        values = Array.isArray(parsed) ? parsed.map(String) : [String(parsed)]
      } catch {
        values = [String(answer)]
      }

      if (values.length === 0) return ''

      // 1) 先按目标题的映射表转换（有映射就覆盖）
      const mapping = this.normalizeOptionMapping(targetQuestion && targetQuestion.optionMapping)
      if (mapping) {
        values = values.map(v => {
          const key = String(v)
          return Object.prototype.hasOwnProperty.call(mapping, key) ? String(mapping[key]) : key
        })
      }

      // 2) 只保留在目标题 options 中真实存在的值，避免脏值
      const targetOptionValues = new Set(((targetQuestion && targetQuestion.options) || []).map(o => String(o.value)))
      if (targetOptionValues.size > 0) {
        values = values.filter(v => targetOptionValues.has(String(v)))
      }

      // 去重
      values = [...new Set(values)]
      if (values.length === 0) return ''

      // 3) 根据目标题型输出格式
      if (this.isMultipleChoice(targetQuestion.itemType)) {
        return JSON.stringify(values)
      }

      // 目标是单选：只取第一个
      return JSON.stringify([values[0]])
    },

    // 获取回圈关联候选键：itemKey / __vModel__ / serialNo（兼容历史配置）
    getLoopbackCandidateKeys(question) {
      const normalize = (v) => {
        if (v === null || v === undefined) return ''
        const s = String(v).trim()
        return (s === 'undefined' || s === 'null') ? '' : s
      }

      const keys = [
        normalize(question?.itemKey),
        normalize(question?.vModel),
        normalize(question?.serialNo)
      ].filter(Boolean)

      return [...new Set(keys)]
    },

    // 标准化 optionMapping
    // 支持格式：
    // 1) 对象：{ "A": "B", "1": "2" }
    // 2) 数组：[{sourceValue:'A', targetValue:'B'}] / [{from:'A', to:'B'}]
    normalizeOptionMapping(raw) {
      if (!raw) return null

      let data = raw
      if (typeof raw === 'string') {
        try {
          data = JSON.parse(raw)
        } catch {
          return null
        }
      }

      if (Array.isArray(data)) {
        const map = {}
        data.forEach(item => {
          if (!item) return
          const from = item.sourceValue ?? item.from ?? item.source ?? item.key
          const to = item.targetValue ?? item.to ?? item.target ?? item.value
          if (from !== undefined && from !== null && to !== undefined && to !== null) {
            map[String(from)] = String(to)
          }
        })
        return Object.keys(map).length ? map : null
      }

      if (typeof data === 'object') {
        const map = {}
        Object.keys(data).forEach(k => {
          const v = data[k]
          if (v !== undefined && v !== null && v !== '') {
            map[String(k)] = String(v)
          }
        })
        return Object.keys(map).length ? map : null
      }

      return null
    },
    
    // 根据审核答案自动更新评审结果
    updateReviewResultFromAnswer(question, reviewAnswer) {
      if (!reviewAnswer) {
        // 没有选择任何选项，不得分
        this.$set(this.reviewMap, question.itemKey, 'NO_SCORE')
        return
      }
      
      // 检查是否选中了“不涉及”选项
      if (this.isNotApplicableAnswer(question, reviewAnswer)) {
        this.$set(this.reviewMap, question.itemKey, 'NOT_APPLICABLE')
        return
      }
      
      // 情况1: 如果每个选项都设置了分值（perOption模式），使用混合计分逻辑
      if (this.hasAllOptionsWithScore(question)) {
        // 计算正分之和与负分之和
        let positiveSum = 0
        let negativeSum = 0
        try {
          let selectedValues = JSON.parse(reviewAnswer)
          if (!Array.isArray(selectedValues)) selectedValues = [selectedValues]
          
          selectedValues.forEach(val => {
            const opt = question.options.find(o => String(o.value) === String(val))
            if (opt && opt.score) {
              if (opt.score > 0) {
                positiveSum += opt.score
              } else {
                negativeSum += opt.score
              }
            }
          })
        } catch {
          // 解析失败，按不得分处理
        }
        
        // 使用混合计分逻辑：只选负分项时从满分扣减
        const fullScore = question.maxScore || 0
        let baseScore = 0
        if (positiveSum === 0 && negativeSum < 0) {
          baseScore = fullScore  // 只选负分项时，从满分扣减
        }
        
        // 公式：Final = min(Total, Base + Pos) + Neg
        let calculated = baseScore + positiveSum
        if (calculated > fullScore) calculated = fullScore
        let earnedScore = calculated + negativeSum
        if (earnedScore < 0) earnedScore = 0
        
        // 只有满分才是SCORE，不满分就是NO_SCORE
        this.$set(this.reviewMap, question.itemKey, earnedScore >= fullScore ? 'SCORE' : 'NO_SCORE')
        return
      }
      
      // 情况2: 有正确答案，比对审核答案与正确答案
      if (question.correctAnswer) {
        const isCorrect = this.checkAnswerCorrect(reviewAnswer, question.correctAnswer, question.scoringMethod)
        this.$set(this.reviewMap, question.itemKey, isCorrect ? 'SCORE' : 'NO_SCORE')
      } else {
        // 情况3: 没有正确答案的题目，不自动设置得分，需要审核人员手动判断
        // 如果当前已有评审结果则保持，否则默认不得分
        const currentResult = this.reviewMap[question.itemKey]
        if (!currentResult) {
          this.$set(this.reviewMap, question.itemKey, 'NO_SCORE')
        }
        // 否则保持原评审结果不变，让用户手动修改
      }
    },
    
    isSelectedOption(question, option) {
      const answer = this.answerMap[question.itemKey]
      if (!answer) return false
      try {
        const selected = JSON.parse(answer)
        if (Array.isArray(selected)) {
          return selected.includes(option.value)
        }
        return selected === option.value
      } catch {
        return String(answer) === String(option.value)
      }
    },
    
    getAnswerValue(itemKey) {
      return this.answerMap[itemKey] || ''
    },
    
    getNavItemClass(question) {
      const result = this.reviewMap[question.itemKey]
      if (!result) return 'unanswered'
      if (result === 'SCORE') return 'correct'
      if (result === 'NO_SCORE') return 'wrong'
      if (result === 'NOT_APPLICABLE') return 'not-applicable'
      return 'unanswered'
    },
    
    scrollToQuestion(index) {
      const ref = this.$refs['question-' + index]
      if (ref && ref[0]) {
        ref[0].scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    scrollToRefItem(phaseNo, itemKey) {
      const ref = this.$refs['ref-item-' + phaseNo + '-' + itemKey]
      if (ref && ref[0]) {
        ref[0].scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    filteredRefItems(group) {
      const keyword = (this.navSearchKeyword || '').trim().toLowerCase()
      if (!keyword) return group.items
      return group.items.filter(item => 
        this.stripHtml(item.label).toLowerCase().includes(keyword)
        || (item.serialNo && item.serialNo.toLowerCase().includes(keyword))
      )
    },
    
    formatDuration(seconds) {
      if (!seconds) return '00:00:00'
      const h = Math.floor(seconds / 3600)
      const m = Math.floor((seconds % 3600) / 60)
      const s = seconds % 60
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    },
    
    stripHtml(str) {
      if (!str) return ''
      return str.replace(/<[^>]+>/g, '').trim()
    },
    
    truncateTitle(str, maxLen) {
      if (!str) return ''
      return str.length > maxLen ? str.substring(0, maxLen) + '...' : str
    },
    
    // 自动检查答案是否正确，返回 SCORE/NO_SCORE/NOT_APPLICABLE
    autoCheckAnswerResult(question) {
      const userAnswer = this.answerMap[question.itemKey]
      
      // 如果没有作答，返回不得分
      if (!userAnswer) {
        return 'NO_SCORE'
      }
      
      // 检查用户是否选了“不涉及”选项
      if (this.isNotApplicableAnswer(question, userAnswer)) {
        return 'NOT_APPLICABLE'
      }
      
      // 情况1：每个选项都设置了分值（perOption 模式），使用混合计分逻辑
      if (this.hasAllOptionsWithScore(question)) {
        // 计算正分之和与负分之和
        let positiveSum = 0
        let negativeSum = 0
        try {
          let selectedValues = []
          try {
            selectedValues = JSON.parse(userAnswer)
            if (!Array.isArray(selectedValues)) selectedValues = [selectedValues]
          } catch {
            selectedValues = [userAnswer]
          }
          
          selectedValues.forEach(val => {
            const opt = question.options.find(o => String(o.value) === String(val))
            if (opt && opt.score) {
              if (opt.score > 0) {
                positiveSum += opt.score
              } else {
                negativeSum += opt.score
              }
            }
          })
        } catch (e) {
        }
        
        // 使用混合计分逻辑：只选负分项时从满分扣减
        const fullScore = question.maxScore || 0
        let baseScore = 0
        if (positiveSum === 0 && negativeSum < 0) {
          baseScore = fullScore  // 只选负分项时，从满分扣减
        }
        
        // 公式：Final = min(Total, Base + Pos) + Neg
        let calculated = baseScore + positiveSum
        if (calculated > fullScore) calculated = fullScore
        let earnedScore = calculated + negativeSum
        if (earnedScore < 0) earnedScore = 0
        
        return earnedScore > 0 ? 'SCORE' : 'NO_SCORE'
      }
      
      // 情况2：有正确答案，根据正确答案判断
      if (question.correctAnswer) {
        const isCorrect = this.checkAnswerCorrect(userAnswer, question.correctAnswer, question.scoringMethod)
        return isCorrect ? 'SCORE' : 'NO_SCORE'
      }
      
      // 情况3：没有正确答案也没有选项分值，需要人工判断
      // 情况3：没有正确答案也没有选项分值，需要人工判断
      return ''  // 需要人工判断
    },
    
    // 比较用户答案和正确答案是否匹配
    // scoringMethod: 'single' | 'allContains' | 'perOption' | 'manual'
    //   - single（默认/多选精确匹配）：用户答案 === 正确答案（排序后全一致）
    //   - allContains（仅多选）：用户答案覆盖全部正确答案即可（允许多选错项）
    checkAnswerCorrect(userAnswer, correctAnswer, scoringMethod) {
      if (!userAnswer || !correctAnswer) {
        return false
      }

      try {
        // 尝试解析为数组（多选题）
        let userArr = []
        let correctArr = []

        try {
          userArr = JSON.parse(userAnswer)
          if (!Array.isArray(userArr)) userArr = [userArr]
        } catch {
          userArr = String(userAnswer).split(',').map(s => s.trim())
        }

        try {
          correctArr = JSON.parse(correctAnswer)
          if (!Array.isArray(correctArr)) correctArr = [correctArr]
        } catch {
          correctArr = String(correctAnswer).split(',').map(s => s.trim())
        }

        const userStrs = userArr.map(String).map(s => s.trim()).filter(Boolean)
        const correctStrs = correctArr.map(String).map(s => s.trim()).filter(Boolean)

        // 包含全部正确答案即得分（多选宽松模式）
        if (scoringMethod === 'allContains') {
          if (correctStrs.length === 0) return false
          const userSet = new Set(userStrs)
          return correctStrs.every(c => userSet.has(c))
        }

        // 默认：精确匹配（single / 未指定）
        const userSorted = [...userStrs].sort()
        const correctSorted = [...correctStrs].sort()
        if (userSorted.length !== correctSorted.length) return false
        return userSorted.every((val, idx) => val === correctSorted[idx])
      } catch (e) {
        // 简单字符串比较
        return String(userAnswer).trim() === String(correctAnswer).trim()
      }
    },

    // 判断答案是否选中了标记为“不涉及”的选项
    isNotApplicableAnswer(question, answerValue) {
      if (!answerValue || !question.options) return false
      // 获取所有标记为 notApplicable 的选项值
      const naOptionValues = question.options
        .filter(opt => opt.notApplicable)
        .map(opt => String(opt.value))
      if (naOptionValues.length === 0) return false
      // 解析答案中选中的选项
      let selectedValues = []
      try {
        const parsed = JSON.parse(answerValue)
        selectedValues = Array.isArray(parsed) ? parsed.map(String) : [String(parsed)]
      } catch {
        selectedValues = [String(answerValue)]
      }
      // 只要选中了任何一个“不涉及”选项，就返回 true
      return selectedValues.some(v => naOptionValues.includes(v))
    },

    // 判断是否为评分类题目
    isRatingQuestion(itemType) {
      if (!itemType) return false
      const type = itemType.toUpperCase()
      return ['RATING', 'RATE', 'SCORE', 'NPS', 'SLIDER'].includes(type)
    },

    // 获取评分题最大分数
    getRatingMax(question) {
      if (question.maxScore) return question.maxScore
      // 尝试解析配置信息
      try {
        const config = JSON.parse(question.configJson || '{}')
        return config.max || config.maxScore || 5
      } catch (e) {
        return 5
      }
    },
    
    // 判断是否为签名题
    isSignatureQuestion(itemType) {
      if (!itemType) return false
      const type = itemType.toUpperCase()
      return ['SIGNATURE', 'SIGN', 'HANDWRITING', 'SIGN_PAD'].includes(type)
    },
    
    // 判断是否为手机号验证题
    isPhoneVerifyQuestion(itemType) {
      if (!itemType) return false
      return ['MOBILE_INPUT', 'PHONE', 'PHONE_VERIFY', 'SMS_VERIFY'].includes(itemType)
    },
    
    // 解析手机号验证答案 JSON
    parsePhoneAnswer(val) {
      if (!val) return { phone: '', code: '' }
      try {
        const parsed = typeof val === 'string' ? JSON.parse(val) : val
        return { phone: parsed.phone || parsed.mobile || '', code: parsed.code || parsed.verifyCode || '' }
      } catch {
        return { phone: String(val), code: '' }
      }
    },
    
    // 判断廉政协议 URL 是否为图片（非 PDF）
    isAgreementImage(url) {
      if (!url) return false
      const urlPath = url.split('?')[0].toLowerCase()
      return /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/.test(urlPath)
    },
    
    // 格式化答案显示（处理可能的 JSON 字符串）
    formatAnswerDisplay(val) {
      if (!val) return ''
      if (typeof val !== 'string') return String(val)
      const trimmed = val.trim()
      // 尝试解析 JSON 对象/数组，格式化展示
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
          const parsed = JSON.parse(trimmed)
          // 如果是文件数组 [{url, name}]，提取名称
          if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].url) {
            return parsed.map(f => f.name || '文件').join('、')
          }
          // 普通对象，格式化为 key: value
          if (typeof parsed === 'object' && !Array.isArray(parsed)) {
            return Object.entries(parsed).map(([k, v]) => `${k}：${v}`).join('，')
          }
        } catch { /* 非法 JSON，原样返回 */ }
      }
      return val
    }
  }
}
</script>

<style lang="scss" scoped>
.exam-grading-page {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f5f7fa;
  overflow: hidden;
  
  /* 自定义滚动条 - 匹配原型图细滚动条 */
  ::v-deep *::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  ::v-deep *::-webkit-scrollbar-track {
    background: transparent;
  }
  ::v-deep *::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.3);
    border-radius: 3px;
    &:hover {
      background: rgba(148, 163, 184, 0.5);
    }
  }
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.3);
    border-radius: 3px;
  }
}

.grading-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  padding: 0 24px;
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
  position: relative;
  flex-shrink: 0;
  z-index: 10;
  
  .title {
    font-size: 16px;
    font-weight: 600;
    color: #5856D6;
    flex-shrink: 0;
  }
  
  .current-dealer-info {
    margin-left: 0;
    margin-right: auto;
    display: flex;
    align-items: center;
    gap: 12px;
    
    .dealer-code {
      background: #2563eb;
      color: #fff;
      padding: 4px 12px;
      border-radius: 4px;
      font-size: 14px;
      font-weight: 700;
    }
    
    .dealer-name {
      font-size: 16px;
      color: #1e293b;
      font-weight: 600;
    }
  }
  
  .review-level-tabs {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 12px;
    
    ::v-deep .el-radio-group {
      background: #f1f5f9;
      padding: 4px;
      border-radius: 8px;
      
      .el-radio-button__inner {
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
        color: #64748b !important;
        font-weight: 500;
        padding: 6px 24px;
        border-radius: 6px !important;
        font-size: 14px;
      }
      
      .el-radio-button.is-active .el-radio-button__inner,
      .el-radio-button__orig-radio:checked + .el-radio-button__inner {
        background: #fff !important;
        color: #2563eb !important;
        box-shadow: 0 1px 3px rgba(0,0,0,0.1) !important;
        font-weight: 600;
      }
    }
    
    .level-hint-wrapper {
      min-width: 120px;
      display: flex;
      align-items: center;
    }
    
    .level-hint {
      font-size: 12px;
      white-space: nowrap;
      
      i {
        margin-right: 4px;
      }
      
      &.readonly-hint {
        color: #909399;
        background: #f4f4f5;
        padding: 4px 8px;
        border-radius: 4px;
      }
      
      &.warning {
        color: #E6A23C;
      }
    }
  }
}

.grading-footer {
  height: 64px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  background: #fff;
  border-top: 1px solid #e2e8f0;
  flex-shrink: 0;
  
  .readonly-text {
    color: #909399;
    font-size: 14px;
    
    i {
      margin-right: 4px;
    }
  }
  
  ::v-deep .el-button {
    border-radius: 8px;
    padding: 10px 28px;
    font-size: 14px;
    font-weight: 500;
    
    &--success {
      background: #22c55e;
      border-color: #22c55e;
      &:hover {
        background: #16a34a;
        border-color: #16a34a;
      }
    }
    
    &--default {
      border-color: #e2e8f0;
      color: #475569;
      &:hover {
        border-color: #cbd5e1;
        color: #334155;
      }
    }
  }
}

.grading-content {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.left-panel {
  width: 256px;
  background: #fff;
  border-right: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  
  .panel-header {
    padding: 16px;
    border-bottom: 1px solid #e2e8f0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    .panel-title {
      font-weight: 700;
      color: #334155;
    }
    
    .progress {
      color: #94a3b8;
      font-size: 12px;
    }
  }
  
  .examinee-list {
    flex: 1;
    overflow-y: auto;
  }
  
  .examinee-item {
    padding: 14px 16px;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    transition: all 0.15s;
    position: relative;
    
    &:hover {
      background: #f8fafc;
    }
    
    &.active {
      background: #eff6ff;
      border: none;
      border-right: 4px solid #3b82f6;
      
      .examinee-name {
        color: #1d4ed8;
        font-weight: 600;
      }
    }
    
    .status-badge {
      font-size: 11px;
      padding: 3px 10px;
      border-radius: 12px;
      font-weight: 500;
      
      &.status-pending { background: #dbeafe; color: #2563eb; }
      &.status-review1 { background: #e6f7ff; color: #1890ff; }
      &.status-review2 { background: #fff7e6; color: #fa8c16; }
      &.status-complete { background: #f6ffed; color: #52c41a; }
      &.status-rejected { background: #fff1f0; color: #f5222d; }
    }
    
    .draft-badge {
      font-size: 10px;
      padding: 2px 6px;
      border-radius: 8px;
      background: #fff7e6;
      color: #d48806;
      border: 1px solid #ffe58f;
      font-weight: 500;
    }
  }
  
  .left-panel-footer {
    padding: 16px;
    background: #f8fafc;
    border-top: 1px solid #e2e8f0;
    
    .el-input__inner {
      border-color: #e2e8f0 !important;
      background: #fff;
      font-size: 14px;
    }
    
    .footer-info {
      margin-top: 12px;
      
      p {
        font-size: 11px;
        color: #64748b;
        margin: 0;
        line-height: 1.8;
      }
    }
  }
}

.center-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #f8fafc;
}

.center-panel {
  flex: 1;
  overflow-y: auto;
  padding: 32px;
  
  .answer-list {
    max-width: 900px;
    margin: 0 auto;
  }
  
  .question-card {
    background: #fff;
    border-radius: 8px;
    padding: 16px 20px;
    margin-bottom: 16px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  }
  
  .question-header {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
    
    .question-title {
      font-weight: 600;
      color: #303133;
    }
    
    .question-score-info {
      margin-left: 10px;
      color: #909399;
      font-size: 13px;
    }
    
    .full-record-badge {
      margin-left: auto;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-size: 13px;
      color: #3b82f6;
      padding: 4px 14px;
      border: 1px solid #bfdbfe;
      border-radius: 6px;
      background: #eff6ff;
      font-weight: 500;
      
      .dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #3b82f6;
      }
    }
    
    .float-play-btn {
      color: #3b82f6 !important;
      font-size: 13px !important;
      font-weight: 500;
      margin-left: 8px;
      padding: 0 !important;
    }
  }
  
  .question-label {
    margin-bottom: 12px;
    color: #606266;
    line-height: 1.6;
  }
  
  .options-list {
    .loopback-hint {
      margin-bottom: 6px;
      padding: 6px 10px;
      font-size: 12px;
      color: #909399;
      background: #f5f7fa;
      border: 1px dashed #dcdfe6;
      border-radius: 4px;
    }

    .option-item {
      display: flex;
      align-items: center;
      padding: 8px 12px;
      margin-bottom: 4px;
      border-radius: 4px;
      position: relative;
      
      &.clickable {
        cursor: pointer;
        transition: all 0.2s;
        
        &:hover {
          background: #ecf5ff;
        }
      }
      
      &.readonly {
        cursor: default;
        opacity: 0.8;
      }
      
      &.is-original-selected {
        background: #fef0f0;
      }
      
      &.is-review-selected {
        background: #ecf5ff;
        border: 2px solid #409EFF;
      }
      
      &.is-correct {
        background: #f0f9eb;
      }
      
      &.is-wrong {
        background: #fef0f0;
      }
      
      .option-marker {
        margin-right: 8px;
        
        .correct-icon {
          color: #67C23A;
        }
        
        .wrong-icon {
          color: #F56C6C;
        }
        
        .review-check {
          color: #409EFF;
          font-weight: bold;
        }
        
        .option-circle {
          display: inline-block;
          width: 14px;
          height: 14px;
          border: 1px solid #dcdfe6;
          border-radius: 50%;
        }
        
        .option-checkbox {
          display: inline-block;
          width: 14px;
          height: 14px;
          border: 1px solid #dcdfe6;
          border-radius: 2px;
        }
      }
      
      .original-tag {
        margin-left: auto;
        padding: 2px 6px;
        font-size: 10px;
        background: #fef0f0;
        color: #F56C6C;
        border-radius: 4px;
      }
      
      .review1-tag {
        margin-left: 4px;
        padding: 2px 6px;
        font-size: 10px;
        background: #e6f7ff;
        color: #1890ff;
        border-radius: 4px;
      }
      
      .review2-tag {
        margin-left: 4px;
        padding: 2px 6px;
        font-size: 10px;
        background: #fff7e6;
        color: #fa8c16;
        border-radius: 4px;
      }
    }
  }
  
  .subjective-answer {
    background: #f5f7fa;
    padding: 12px;
    border-radius: 4px;
    
    .answer-label {
      color: #909399;
      font-size: 12px;
      margin-bottom: 8px;
    }
    
    .answer-content {
      color: #303133;
      line-height: 1.6;
    }
  }
  
  // 录音题样式
  .audio-answer {
    .answer-label {
      color: #606266;
      font-size: 13px;
      font-weight: 500;
      margin-bottom: 12px;
    }
    
    .answer-content {
      margin-top: 8px;
    }
  }
  
  .review-section {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px dashed #e4e7ed;
    
    .review-options {
      display: flex;
      align-items: center;
      margin-bottom: 12px;
      
      .review-label {
        color: #606266;
        margin-right: 12px;
        font-weight: 500;
      }
    }
    
    .review-remark {
      margin-top: 8px;
    }
  }
  
  .empty-tip {
    text-align: center;
    padding: 100px 0;
    color: #909399;
  }
}

.right-panel {
  width: 280px;
  background: #fff;
  border-left: 1px solid #e2e8f0;
  padding: 24px 20px;
  display: flex;
  flex-direction: column;
  
  .score-summary {
    text-align: center;
    margin-bottom: 16px;
    
    .label {
      display: block;
      color: #94a3b8;
      font-size: 13px;
      margin-bottom: 4px;
    }
    
    .score {
      font-size: 36px;
      font-weight: 700;
      color: #1e293b;
      line-height: 1.2;
      
      &.original {
        color: #1e293b;
      }
      
      &.changed {
        color: #67C23A;
      }
    }
    
    &.review-score .score {
      color: #ef4444;
      font-size: 40px;
    }
  }
  
  .level-display {
    text-align: center;
    margin-bottom: 24px;
    
    .level-label {
      color: #94a3b8;
      font-size: 13px;
    }
    
    .level-value {
      display: inline-block;
      font-size: 14px;
      font-weight: 700;
      margin-left: 8px;
      padding: 4px 16px;
      border-radius: 20px;
      background: #fef2f2;
      color: #ef4444 !important;
    }
  }
  
  .simplified-title {
    text-align: center;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid #e2e8f0;
    
    .label {
      color: #606266;
      font-size: 14px;
      font-weight: 500;
    }
  }
  
  .nav-search {
    padding: 0 4px 12px;
    
    .el-input {
      .el-input__inner {
        border-radius: 8px;
        border-color: #e2e8f0;
        font-size: 12px;
        &:focus {
          border-color: #3b82f6;
        }
      }
    }
  }
  
  .question-nav {
    flex: 1;
    overflow-y: auto;
    margin-bottom: 20px;
    padding-top: 12px;
    border-top: 1px solid #e2e8f0;
    
    &.nav-list-mode {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    
    .nav-phase-title {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 12px;
      font-weight: 600;
      color: #64748b;
      padding: 4px 8px;
      margin-top: 4px;
      cursor: pointer;
      user-select: none;
      
      &:hover { color: #475569; }
      
      .nav-collapse-arrow {
        font-size: 12px;
        transition: transform 0.2s;
        color: #94a3b8;
        
        &.expanded {
          transform: rotate(90deg);
        }
      }
      
      .nav-phase-badge {
        width: 18px;
        height: 18px;
        border-radius: 50%;
        background: #2563eb;
        color: #fff;
        font-size: 10px;
        font-weight: 700;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      }
      
      .nav-phase-count {
        margin-left: auto;
        font-size: 11px;
        color: #94a3b8;
        font-weight: 400;
      }
    }
    
    .nav-ref-item {
      background: #f8fafc;
      border-color: #e2e8f0;
      border-style: dashed;
      
      &:hover {
        background: #eff6ff;
        border-color: #93c5fd;
      }
      
      .nav-index { color: #94a3b8; }
      .nav-title { color: #94a3b8; }
    }
    
    .nav-ref-tag {
      flex-shrink: 0;
      font-size: 10px;
      padding: 1px 6px;
      border-radius: 4px;
      background: #eff6ff;
      color: #3b82f6;
      margin-left: auto;
    }
    
    .nav-section-divider {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 8px 0;
      font-size: 11px;
      color: #94a3b8;
      
      &::before,
      &::after {
        content: '';
        flex: 1;
        height: 1px;
        background: #e2e8f0;
      }
    }
    
    .nav-list-item {
      display: flex;
      align-items: center;
      padding: 12px 14px;
      border-radius: 10px;
      border: 1px solid #e2e8f0;
      font-size: 13px;
      cursor: pointer;
      transition: all 0.15s;
      
      &:hover {
        border-color: #cbd5e1;
        background: #f8fafc;
      }
      
      .nav-index {
        flex-shrink: 0;
        min-width: 32px;
        color: #64748b;
        font-weight: 600;
      }
      
      .nav-title {
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: #475569;
      }
      
      .nav-status {
        flex-shrink: 0;
        margin-left: 8px;
      }
      
      .nav-pass {
        display: inline-block;
        font-size: 11px;
        padding: 3px 12px;
        border-radius: 6px;
        background: #22c55e;
        color: #fff;
        font-weight: 600;
      }
      
      .nav-fail {
        display: inline-block;
        font-size: 11px;
        padding: 3px 12px;
        border-radius: 6px;
        background: #ef4444;
        color: #fff;
        font-weight: 600;
      }
      
      &.unanswered {
        background: #fff;
        border-color: #e2e8f0;
        .nav-title { color: #94a3b8; }
        &::after {
          content: '未打分';
          font-size: 11px;
          color: #94a3b8;
          margin-left: auto;
          flex-shrink: 0;
        }
      }
      
      &.correct {
        background: #f0fdf4;
        border-color: #bbf7d0;
        border-left: 4px solid #22c55e;
        .nav-index { color: #16a34a; }
        .nav-title { color: #15803d; }
      }
      
      &.wrong {
        background: #fef2f2;
        border-color: #fecaca;
        border-left: 4px solid #ef4444;
        .nav-index { color: #dc2626; }
        .nav-title { color: #b91c1c; }
      }
      
      &.not-applicable {
        background: #f8fafc;
        border-color: #e2e8f0;
        border-left: 4px solid #94a3b8;
        .nav-index { color: #94a3b8; }
      }
    }
  }
  
  .nav-buttons {
    margin-top: auto;
    display: flex;
    gap: 12px;
    padding-top: 16px;
    border-top: 1px solid #e2e8f0;
    
    ::v-deep .el-button {
      flex: 1;
      border-radius: 8px;
      height: 40px;
      font-size: 14px;
      border-color: #e2e8f0;
      color: #475569;
      font-weight: 500;
    }
  }
}

// Question 组件适配样式
.question-display-wrapper {
  position: relative;
  
  // 隐藏 Question 组件内的标题（审核页面有自己的标题）
  ::v-deep .question-item {
    .question-header {
      display: none;
    }
    
    .question-desc,
    .question-tip {
      display: none;
    }
  }
}

// 原答案标识样式
.original-answer-markers {
  margin-top: 12px;
  padding: 10px 12px;
  background: #fef0f0;
  border-radius: 4px;
  border-left: 3px solid #F56C6C;
  
  .original-answer-marker {
    display: block;
    color: #F56C6C;
    font-size: 13px;
    line-height: 1.6;
    
    &:not(:last-child) {
      margin-bottom: 4px;
    }
  }
}

// 文件上传展示样式
.file-upload-answer {
  .answer-label {
    color: #303133;
    font-weight: 500;
    margin-bottom: 8px;
  }
  
  .file-gallery {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }
  
  .file-thumb {
    border-radius: 4px;
    overflow: hidden;
    
    &.image-thumb {
      width: 100px;
      height: 100px;
      cursor: pointer;
      
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
    
    &.video-thumb {
      max-width: 320px;
    }
    
    &.audio-thumb {
      display: flex;
      flex-direction: column;
      gap: 4px;
      
      .audio-name {
        font-size: 12px;
        color: #909399;
      }
    }
  }
  
  .file-download-link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 8px 12px;
    background: #f5f7fa;
    border-radius: 4px;
    color: #409eff;
    text-decoration: none;
    
    &:hover {
      background: #ecf5ff;
    }
    
    i {
      font-size: 16px;
    }
  }
  
  .no-file {
    color: #909399;
    font-style: italic;
  }
  
  /* 评分显示样式 */
  .rating-display {
    display: flex;
    align-items: center;
    
    ::v-deep .el-rate {
      display: inline-flex;
      align-items: center;
    }
    
    ::v-deep .el-rate__icon {
      font-size: 18px;
      margin-right: 2px;
    }
  }
}

// "其他"选项文本样式
.other-text-value {
  color: #e6a23c;
  font-weight: 500;
  margin-left: 4px;
}

// "其他"选项输入框样式
.other-input-wrapper {
  margin-left: 28px;  // 与选项文字对齐（option-marker宽度）
  margin-top: 8px;
  margin-bottom: 8px;
  
  .other-input {
    max-width: 300px;
  }
}

/* ===== 跨阶段参考资料区域 ===== */
.cross-phase-reference {
  margin-bottom: 32px;
}

.ref-section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 16px;
  i { font-size: 18px; color: #94a3b8; }
}

/* 原型图风格：白色圆角卡片 + 阴影 */
.ref-phase-group {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  &:last-child { margin-bottom: 0; }
}

/* badge 和标题在同一行 */
.ref-phase-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
}

.ref-phase-badge {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #2563eb;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 0;
}

/* 不同阶段用不同颜色（原型图统一蓝色） */
.ref-phase-group:nth-child(2) .ref-phase-badge { background: #2563eb; }
.ref-phase-group:nth-child(3) .ref-phase-badge { background: #2563eb; }
.ref-phase-group:nth-child(4) .ref-phase-badge { background: #2563eb; }

.ref-phase-name {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
}

.ref-phase-time {
  font-size: 12px;
  color: #94a3b8;
  margin-left: auto;
}

.ref-question-card {
  padding: 0;
  margin-bottom: 24px;
  &:last-child { margin-bottom: 0; }
}

.ref-question-title {
  font-size: 14px;
  font-weight: 500;
  color: #2563eb;
  margin-bottom: 8px;
  line-height: 1.5;
}

.ref-serial {
  color: #2563eb;
  font-weight: 600;
  margin-right: 2px;
}

.ref-answer-area {
  font-size: 14px;
  color: #303133;
}

.ref-option {
  padding: 8px 12px;
  margin: 4px 0;
  border-radius: 8px;
  font-size: 14px;
  color: #94a3b8;
  display: flex;
  align-items: center;
  gap: 8px;
  
  .ref-option-circle {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 1px solid #cbd5e1;
    flex-shrink: 0;
  }
  
  .ref-check-icon {
    width: 16px;
    height: 16px;
    color: #2563eb;
    flex-shrink: 0;
  }
  
  &.selected {
    color: #2563eb;
    background: rgba(59,130,246,0.04);
    font-weight: 500;
    border: 1px solid rgba(59,130,246,0.15);
    border-radius: 8px;
    padding: 8px 12px;
  }
}

.ref-signature-container {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  width: 250px;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.2s;
  
  &:hover {
    border-color: #cbd5e1;
  }
  
  img {
    max-width: 100%;
    max-height: 100px;
    object-fit: contain;
    opacity: 0.8;
  }
}

.ref-text-answer {
  display: block;
  padding: 12px 16px;
  background: #f8fafc;
  border: 1px solid #f1f5f9;
  border-radius: 8px;
  font-size: 14px;
  color: #1e293b;
  word-break: break-all;
  line-height: 1.6;
}

.ref-signature-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  width: fit-content;
  max-width: 256px;
  img {
    max-height: 128px;
    object-fit: contain;
    cursor: pointer;
    opacity: 0.8;
    mix-blend-mode: multiply;
  }
}

.ref-no-answer {
  color: #94a3b8;
  font-size: 13px;
  font-style: italic;
}

.ref-file-gallery {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
}

.ref-file-thumb {
  width: 80px;
  height: 80px;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #e4e7ed;
  cursor: pointer;
  position: relative;
  transition: box-shadow 0.2s;
  &:hover { box-shadow: 0 2px 12px rgba(0,0,0,0.12); }
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.ref-thumb-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
  i { color: #fff; font-size: 20px; }
  .ref-file-thumb:hover & { opacity: 1; }
}

.ref-video-thumb, .ref-audio-thumb {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: #f5f7fa;
  i { font-size: 24px; color: #409eff; }
  span { font-size: 10px; color: #909399; text-align: center; overflow: hidden; text-overflow: ellipsis; max-width: 70px; white-space: nowrap; }
  &:hover { background: #ecf5ff; }
}

.ref-file-link {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  background: #f5f7fa;
  border-radius: 4px;
  font-size: 12px;
  color: #409eff;
  text-decoration: none;
  &:hover { background: #ecf5ff; }
}

.cross-phase-loading {
  text-align: center;
  padding: 16px;
  color: #909399;
  font-size: 13px;
}

/* 当前审核标记分隔线 */
.current-audit-marker {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 16px 0;
  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #409eff;
    opacity: 0.3;
  }
}

.audit-marker-badge {
  background: #409eff;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 12px;
  white-space: nowrap;
}

.audit-marker-text {
  font-size: 12px;
  color: #409eff;
  font-weight: 500;
  white-space: nowrap;
}

/* 廉政协议题 */
.agreement-answer-section {
  .answer-label {
    font-size: 13px;
    color: #909399;
    margin-bottom: 8px;
  }
  .agreement-pdf-pages {
    width: 100%;
    border: 1px solid #ebeef5;
    border-radius: 8px;
    overflow: hidden;
    padding: 4px;
    background: #f5f5f5;
    margin-bottom: 10px;
    .pdf-loading, .pdf-error {
      padding: 24px; text-align: center; color: #909399; font-size: 14px;
      i { margin-right: 6px; }
    }
    .pdf-error { color: #e6a23c; }
  }
  .agreement-check-status {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 8px;
    padding: 8px 0;
    white-space: nowrap;
    i { font-size: 16px; }
    span { font-size: 14px; color: #606266; }
  }
}

/* 明检分类题型 */
.mingjian-answer-section {
  margin-top: 8px;
}
.mingjian-photos {
  margin-bottom: 12px;
}
.mingjian-section-label {
  font-size: 13px;
  font-weight: 500;
  color: #303133;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
  i { color: #409EFF; }
}
.mingjian-result {
  margin-top: 8px;
}
</style>
